import { useEffect, useRef, useState } from "react";
import VoiceToPunchModal from "@/components/VoiceToPunchModal";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fileUrl, mediaSrc, formatApiErrorDetail } from "@/lib/api";
import { formatUnit } from "@/lib/formatters";
import { WorkerShell } from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, Images, MapPin, Clock, Camera, Warning, CheckCircle, Receipt,
  Package, ChatCircle, Play, Stop, Plus, Trash, PaperPlaneTilt, Sparkle, Notepad,
  HandGrabbing, ListChecks, Square, CheckSquare, VideoCamera, PaintBrush, FilePdf,
  Signature, PencilSimple, Barcode, CloudRain, X, ClipboardText, ArrowUUpLeft
} from "@phosphor-icons/react";
import PhotoAnnotator from "@/components/PhotoAnnotator";
import VoiceRecorder from "@/components/VoiceRecorder";
import SignaturePad from "@/components/SignaturePad";
import NeedsDocsBanner from "@/components/NeedsDocsBanner";
import QuickUpdateCard from "@/components/QuickUpdateCard";
import DuplicatesBanner from "@/components/DuplicatesBanner";
import AutoDiagnoseBanner from "@/components/AutoDiagnoseBanner";
import TenantHistoryPanel from "@/components/TenantHistoryPanel";
import ChargebackPanel from "@/components/ChargebackPanel";
import InspectionWizard from "@/components/InspectionWizard";
import InspectionFlowSimple from "@/components/InspectionFlowSimple";
import RoomTagPickerModal from "@/components/RoomTagPickerModal";
import BulkPhotoTray from "@/components/BulkPhotoTray";
import BatchPhotoImport from "@/components/BatchPhotoImport";
import BulkApplianceScan from "@/components/BulkApplianceScan";
import VoiceWalk from "@/components/VoiceWalk";
import { compressImage } from "@/lib/compress";
import PropertyHistoryPanel from "@/components/PropertyHistoryPanel";
import PropertyNotesCard from "@/components/PropertyNotesCard";
import AccessRequestButton from "@/components/AccessRequestButton";
import InlineWoFix from "@/components/InlineWoFix";
import JobMessages from "@/components/JobMessages";
import HabitabilityCountdown from "@/components/HabitabilityCountdown";
import { ReturnLaterPanel, fmtShort } from "@/components/ReturnLater";
import TriageLinkCard from "@/components/TriageLinkCard";
import { API } from "@/lib/api";
import { useT } from "@/i18n";
import PhotoOpen from "@/components/PhotoOpen";
import LegalProtocolPanel from "@/components/LegalProtocolPanel";
import DraftUpdateModal from "@/components/DraftUpdateModal";
import JobCoverageBanner from "@/components/JobCoverageBanner";
import AITriageButton from "@/components/AITriageButton";
import RequestPhotosModal from "@/components/RequestPhotosModal";
import VendorSpendPill from "@/components/VendorSpendPill";
import ESignPanel from "@/components/ESignPanel";
import PurgeFlagModal from "@/components/PurgeFlagModal";
import ApplianceIQChip from "@/components/ApplianceIQChip";
import ShowUpCard from "@/components/ShowUpCard";
import FundingGuardBanner from "@/components/FundingGuardBanner";
import PestTrapGuidePanel from "@/components/PestTrapGuidePanel";
import KraaftJobPanel from "@/components/KraaftJobPanel";
import JobHistoryTimeline from "@/components/JobHistoryTimeline";
import SimpleJobView from "@/components/SimpleJobView";
import { useSimpleMode } from "@/lib/simpleMode";

// Haversine distance in meters between two lat/lng points
function haversine(lat1, lng1, lat2, lng2) {
  const R = 6371000; // Earth radius m
  const toRad = (d) => d * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat/2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng/2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

export default function JobDetail() {
  const { t } = useT();
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { simpleMode } = useSimpleMode();
  const [job, setJob] = useState(null);
  const [tab, setTab] = useState("work");
  const [batchImport, setBatchImport] = useState(null); // null | "before" | "after"
  const [busy, setBusy] = useState(false);
  const [annotate, setAnnotate] = useState(null); // {path}
  const [requestPhotos, setRequestPhotos] = useState(null); // { reason } | null
  const [returnOpen, setReturnOpen] = useState(false);
  const [showSig, setShowSig] = useState(false);
  const [inspectionPhase, setInspectionPhase] = useState(null); // "move_out" | "move_in" | null
  const [releasing, setReleasing] = useState(false);
  const [tenant, setTenant] = useState(null);
  const [arrived, setArrived] = useState(false); // GPS says we're on-site
  const [arrivalDismissed, setArrivalDismissed] = useState(false);
  const [exitPrompt, setExitPrompt] = useState(false); // Geofence exit modal
  const [followUpOpen, setFollowUpOpen] = useState(false); // Follow-up reason picker
  const [purgeOpen, setPurgeOpen] = useState(false); // Bulk-trash / truck-day flag modal
  const exitPromptCooldownUntil = useRef(0); // ms epoch — suppress prompt until this time
  const wasOnSiteRef = useRef(false); // did we ever detect on-site presence this session
  // Room-tag picker state — declared before any early return so hooks stay in order.
  // Only prompts on inspection jobs. Untagged photos still upload (they land under
  // "Additional Photos" in the report) but tagging is strongly nudged.
  const [roomPicker, setRoomPicker] = useState(null); // { files, kind, resolve } | null
  const [stickyRoom, setStickyRoom] = useState(null); // last-picked sweep room, auto-tags subsequent uploads
  const [voicePunch, setVoicePunch] = useState(null);
  const [showAiDraft, setShowAiDraft] = useState(false); // project | null
  const openVoicePunch = async () => {
    if (!job?.property_address) { toast.error("No property on this job"); return; }
    try {
      const r = await api.get("/projects");
      const norm = (s) => (s || "").trim().toLowerCase();
      const match = (r.data?.projects || []).find(p => norm(p.property_address) === norm(job.property_address));
      if (!match) { toast.error("No ProjectRadar project at this property"); return; }
      setVoicePunch(match);
    } catch { toast.error("Couldn't load projects"); }
  };

  const load = () => api.get(`/jobs/${id}`).then(r => setJob(r.data));
  const [openAR, setOpenAR] = useState(null);
  const loadOpenAR = async () => {
    try {
      const { data } = await api.get(`/jobs/${id}/access-requests`);
      const open = (data || []).find(a => a.status === "open");
      setOpenAR(open || null);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); loadOpenAR(); }, [id]);
  useEffect(() => { const t = setInterval(loadOpenAR, 15000); return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  useEffect(() => { api.get("/tenant").then(r => setTenant(r.data)).catch(() => {}); }, []);

  // Arrival detection: if worker is within ~120m of the property and not clocked in, show banner
  useEffect(() => {
    if (!job || !job.geocoded || job.clock_in_at || job.status === "completed") return;
    if (!navigator.geolocation) return;
    let cancelled = false;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (cancelled) return;
        const d = haversine(pos.coords.latitude, pos.coords.longitude, job.geocoded.lat, job.geocoded.lng);
        if (d <= 120) setArrived(true);
      },
      () => {}, { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
    );
    return () => { cancelled = true; };
  }, [job?.id, job?.geocoded, job?.clock_in_at, job?.status]);

  // Geofence exit detection: worker is clocked in and has drifted > 250m from the property.
  // Prompt them to Clock Out or flag as Material Run (keeps timer running).
  // Re-prompts every 15 minutes if still off-site and still clocked in (user setting: 3b).
  useEffect(() => {
    if (!job || !job.geocoded) return;
    if (!job.clock_in_at || job.clock_out_at) return;
    if (job.status === "completed") return;
    if (!navigator.geolocation) return;
    let cancelled = false;
    const check = (pos) => {
      if (cancelled) return;
      const d = haversine(pos.coords.latitude, pos.coords.longitude, job.geocoded.lat, job.geocoded.lng);
      if (d <= 120) {
        // Back on-site — reset cooldown so the next departure re-prompts
        wasOnSiteRef.current = true;
        exitPromptCooldownUntil.current = 0;
        return;
      }
      // Only prompt after we've seen them on-site (avoids nag when they open the app from far away)
      if (!wasOnSiteRef.current) return;
      if (d < 250) return;
      if (Date.now() < exitPromptCooldownUntil.current) return;
      setExitPrompt(true);
    };
    const err = () => {};
    // Prime — mark on-site immediately if already there
    navigator.geolocation.getCurrentPosition(check, err, { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 });
    const iv = setInterval(() => {
      navigator.geolocation.getCurrentPosition(check, err, { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 });
    }, 30000); // check every 30s
    return () => { cancelled = true; clearInterval(iv); };
  }, [job?.id, job?.geocoded, job?.clock_in_at, job?.clock_out_at, job?.status]);

  // Auto-resume live GPS streaming when the tech is en-route but not yet
  // clocked in. Fixes the case where a tech tapped "On my way", closed the
  // app, and came back — without this, the admin map pin would freeze at
  // clock-in location. Stops on unmount or clock-in.
  useEffect(() => {
    if (!job) return;
    const enRouteActive = !!job.en_route_at && !job.clock_in_at && job.status !== "completed";
    if (enRouteActive) {
      startLocationWatch();
    } else {
      stopLocationWatch();
    }
    return () => stopLocationWatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, job?.en_route_at, job?.clock_in_at, job?.status]);


  const doMaterialRun = async () => {
    setBusy(true);
    const send = async (lat, lng) => {
      try {
        const r = await api.post(`/jobs/${id}/material-run`, { kind: "start", lat, lng });
        setJob(r.data);
        toast.success(t("job.material_run_logged"));
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not log material run");
      } finally {
        setBusy(false);
        setExitPrompt(false);
        // Snooze the exit prompt for 15 minutes
        exitPromptCooldownUntil.current = Date.now() + 15 * 60 * 1000;
      }
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => send(p.coords.latitude, p.coords.longitude),
        () => send(null, null),
        { timeout: 5000 }
      );
    } else send(null, null);
  };

  const dismissExitPrompt = () => {
    setExitPrompt(false);
    // Snooze 15 min per user setting (3b)
    exitPromptCooldownUntil.current = Date.now() + 15 * 60 * 1000;
  };

  if (!job) return <WorkerShell><div className="p-8 font-bold">{t("job.loading")}</div></WorkerShell>;

  // Simple Mode: workers who just want camera + note + done get a stripped
  // view. Admins always see the full JobDetail regardless (they need the
  // clock/materials/timeline tabs for oversight).
  if (simpleMode && user?.role === "worker") {
    return <WorkerShell><SimpleJobView job={job} onReload={load}/></WorkerShell>;
  }

  // Bulk Photo Tray is rendered inline within the job page below.
  const BASE_URL_FOR_TRAY = api.defaults.baseURL?.replace(/\/api$/, "") || "";

  // Start streaming the tech's live GPS to the backend so the admin Live
  // Field map + tenant ETA link both see the pin move while they drive.
  // Fires the moment they tap an En-Route ETA chip; stops on clock-out.
  const startLocationWatch = () => {
    if (!navigator.geolocation) return;
    if (window._enrouteWatch != null) return;  // already running
    window._enrouteWatch = navigator.geolocation.watchPosition(async (pos) => {
      try {
        await api.patch(`/jobs/${id}/tech-location`, {
          lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy,
        });
      } catch {}
    }, () => {}, { enableHighAccuracy: true, maximumAge: 15000 });
  };
  const stopLocationWatch = () => {
    if (window._enrouteWatch != null && navigator.geolocation) {
      try { navigator.geolocation.clearWatch(window._enrouteWatch); } catch {}
      window._enrouteWatch = null;
    }
  };

  const doClockIn = async () => {
    setBusy(true);
    const send = async (lat, lng, overrides = {}) => {
      try {
        const r = await api.post(`/jobs/${id}/clock-in`, {
          lat, lng,
          override_funding_gate: !!overrides.funding,
          override_location_gate: !!overrides.location,
        });
        setJob(r.data);
        toast.success(t("toast.clocked_in"));
      } catch (e) {
        const d = e.response?.data?.detail;
        // Funding guard — offer explicit override
        if (e.response?.status === 409 && d?.code === "funding_pending") {
          const total = ((d.landlord_total_cents || 0) / 100).toFixed(2);
          const ok = window.confirm(
            `⚠️ FUNDING PENDING\n\n${d.message}\n\n` +
            `Signed by: ${d.signed_by || "landlord"}\n` +
            `Quote total: $${total}\n\n` +
            `Are you SURE funds have cleared? This override is logged.\n\n` +
            `Tap OK to clock in anyway, or Cancel to check first.`
          );
          if (ok) return send(lat, lng, { ...overrides, funding: true });
          setBusy(false);
          return;
        }
        // Location gate — tell the tech WHY and how to fix. Admins get an
        // override option so they can rescue a stuck field tech.
        if (e.response?.status === 422 && (d?.code === "location_off" || d?.code === "location_far")) {
          if (user?.role === "admin") {
            const ok = window.confirm(`${d.message}\n\nAdmin: override just this once?`);
            if (ok) return send(lat, lng, { ...overrides, location: true });
          } else {
            toast.error(d.message || "Location required to clock in.");
          }
          setBusy(false);
          return;
        }
        toast.error(formatApiErrorDetail(d));
      } finally { setBusy(false); }
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => send(p.coords.latitude, p.coords.longitude),
        () => send(null, null),
        // High accuracy so the 500 m proximity check works — 5 s is too short
        // when the device is warming up its GPS chip on first fix of the day.
        { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 }
      );
    } else send(null, null);
  };

  const doClockOut = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/jobs/${id}/clock-out`);
      setJob(r.data); toast.success(t("toast.clocked_out"));
      stopLocationWatch();
    } finally { setBusy(false); }
  };

  const doComplete = async (status) => {
    // Prefer polished write-up if available, else raw work performed, else prompt
    const wp = job.work_performed_polished || job.work_performed || "";
    let notes = wp;
    if (status === "follow_up") {
      // Structured follow-up: modal picks the reason + extra notes
      setFollowUpOpen(true);
      return;
    }
    if (!notes) {
      const p = prompt("Completion notes — what did you do? (min 30 characters — parts used, what was fixed, anything left)", "");
      if (p === null) return;
      notes = p;
    }

    // Enforce ≥30 chars client-side too so techs get instant feedback instead
    // of a round-trip 422. Admins are exempt (backend also skips the gate for
    // them). Keep looping until they either give up (Cancel) or type enough.
    if (status === "completed" && user?.role === "worker") {
      while ((notes || "").trim().length < 30) {
        const p = prompt(
          `Completion notes are too short (${(notes || "").trim().length}/30 chars). ` +
          `Say what you did — parts used, what you fixed, any issues left.`,
          notes || ""
        );
        if (p === null) return;
        notes = p;
      }
    }

    // Minimum service call suggestion — if job ran under the tenant's minimum threshold, offer to bill the flat minimum
    if (status === "completed" && tenant && job.clock_in_at && !job.is_turn) {
      const startedAt = new Date(job.clock_in_at).getTime();
      const endedAt = job.clock_out_at ? new Date(job.clock_out_at).getTime() : Date.now();
      const minutes = Math.max(0, (endedAt - startedAt) / 60000);
      const threshold = tenant.min_service_call_minutes || 45;
      const charge = tenant.min_service_call_charge || 125;
      if (minutes > 0 && minutes < threshold) {
        const apply = window.confirm(
          `This job only ran ${Math.round(minutes)} min — under the ${threshold}-min minimum.\n\n` +
          `Bill the $${charge.toFixed(0)} minimum service call?\n\n` +
          `OK = Yes, bill the minimum\nCancel = No, keep as-is`
        );
        if (apply) {
          notes = `Minimum service call applied ($${charge.toFixed(0)}, ${Math.round(minutes)} min actual under ${threshold}-min minimum).\n\n${notes}`;
        }
      }
    }

    try {
      const r = await api.post(`/jobs/${id}/complete`, { status, notes });
      setJob(r.data);
      const cascadedCount = r.data?.cascaded_children_closed || 0;
      toast.success(
        status === "completed"
          ? (cascadedCount > 0
              ? `Job completed · ${cascadedCount} consolidated ticket${cascadedCount === 1 ? "" : "s"} also closed`
              : "Job completed")
          : "Marked follow-up",
        {
        duration: 10000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              const rr = await api.post(`/jobs/${id}/reopen`);
              setJob(rr.data);
              toast.success("Reopened — back to work");
            } catch (e) {
              toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not undo");
            }
          },
        },
      });
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Photo Gate — surface a friendly rescue prompt with a one-tap override for admins
      if (detail && typeof detail === "object" && detail.code === "photo_gate") {
        const msg = `📸 ${detail.message}\n\n(Ask an admin to override if the photo really can't be taken.)`;
        toast.error(msg, { duration: 8000 });
        return;
      }
      if (detail && typeof detail === "object" && detail.code === "receipt_gate") {
        toast.error(`🧾 ${detail.message}`, { duration: 9000 });
        document.getElementById("receipts-section")?.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      // Notes gate — server-side rejection. Reopen the prompt so the tech can
      // extend without losing what they typed.
      if (detail && typeof detail === "object" && detail.code === "notes_gate") {
        const extended = prompt(
          `📝 ${detail.message}\n\nAdd more detail:`,
          notes || ""
        );
        if (extended && extended.trim().length >= (detail.min_chars || 30)) {
          return doComplete(status); // caller state unchanged; recurse to reuse polished write-up logic
        }
        toast.error(`Still under ${detail.min_chars || 30} chars — job not closed.`);
        return;
      }
      toast.error(formatApiErrorDetail(detail));
    }
  };

  // FollowUp modal handler — records the structured reason + notes on the job
  const submitFollowUp = async ({ reason, notes }) => {
    try {
      const r = await api.post(`/jobs/${id}/complete`, { status: "follow_up", notes: notes || "", follow_up_reason: reason || "other" });
      setJob(r.data);
      setFollowUpOpen(false);
      toast.success("Marked follow-up", {
        duration: 10000,
        action: {
          label: "Undo",
          onClick: async () => {
            try { const rr = await api.post(`/jobs/${id}/reopen`); setJob(rr.data); toast.success("Reopened"); }
            catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not undo"); }
          },
        },
      });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };


  const uploadPhoto = async (file, kind, roomOverride = null) => {
    // Compress client-side before uploading. iPhone photos average 8 MB;
    // 1920px JPEG @ q=0.82 lands ~500 KB with no visible loss on inspection reports.
    // This is the #1 speed win on cellular during a live walk-through.
    const isVideo = file.type?.startsWith("video/");
    const toSend = isVideo ? file : await compressImage(file);
    const fd = new FormData();
    fd.append("file", toSend);
    if (roomOverride && (kind === "before" || kind === "after")) {
      // Use the room-scoped inspection endpoint so photos get tagged with room + phase.
      // This makes them show up per-room in the finished report + Turn PDF.
      const phase = kind === "before" ? "move_out" : "move_in";
      fd.append("room", roomOverride);
      await api.post(`/jobs/${id}/inspection/${phase}/room/photo`, fd, { headers: { "Content-Type": "multipart/form-data" }});
    } else {
      fd.append("kind", kind);
      await api.post(`/jobs/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" }});
    }
    const label = file.type?.startsWith("video/") ? "Video" : (kind === "annotated" ? "Marked-up photo" : `${kind} photo`);
    toast.success(`${label} uploaded${roomOverride ? ` · ${roomOverride}` : ""}`);
  };

  // Room-tag picker state — only prompts on inspection jobs. Untagged photos still upload
  // (they land under "Additional Photos" in the report), but tagging is strongly nudged.
  const askRoomTag = (files, kind) => new Promise((resolve) => {
    setRoomPicker({ files, kind, resolve });
  });
  const handleRoomPick = (room) => {
    if (roomPicker) {
      roomPicker.resolve(room);
      setRoomPicker(null);
    }
  };
  const handleRoomCancel = () => {
    if (roomPicker) {
      roomPicker.resolve(undefined); // undefined = fully cancel (don't upload)
      setRoomPicker(null);
    }
  };

  // Last picked room within this session — lets rapid-fire uploads auto-tag to the
  // same room without prompting every single tap. Cleared when user hits Skip.
  const uploadPhotos = async (files, kind) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files);
    // Prompt for a room on inspection jobs (Turns or single-phase Move-In/Move-Out)
    let roomTag = null;
    const isInspection = !!(job?.is_turn || job?.inspection_type);
    const availableRooms = job?.rooms || [];
    if (isInspection && availableRooms.length > 0 && (kind === "before" || kind === "after")) {
      if (stickyRoom && availableRooms.includes(stickyRoom)) {
        // Reuse the last picked room — no modal, no interruption. Tradesman keeps shooting.
        roomTag = stickyRoom;
      } else {
        const choice = await askRoomTag(arr, kind);
        if (choice === undefined) return; // user closed modal — cancel batch entirely
        roomTag = choice; // string room OR null (skip untagged)
        if (roomTag) setStickyRoom(roomTag);
      }
    }
    if (arr.length === 1) {
      await uploadPhoto(arr[0], kind, roomTag);
      await load();
      return;
    }
    // Multi-upload: fire in parallel with a generous concurrency cap.
    // Bumped 3→6 since photos are now client-side compressed (~500 KB avg)
    // which lets us push more parallel PUTs without timing out on cellular.
    const total = arr.length;
    let done = 0, failed = 0;
    const CONCURRENCY = 6;
    const queue = arr.slice();
    const workers = Array.from({ length: Math.min(CONCURRENCY, queue.length) }, async () => {
      while (queue.length) {
        const f = queue.shift();
        try {
          await uploadPhoto(f, kind, roomTag);
        } catch (_) { failed++; }
        done++;
      }
    });
    const tid = toast.loading(`Uploading ${total} files…`);
    await Promise.all(workers);
    toast.dismiss(tid);
    if (failed) toast.error(`${failed} of ${total} failed`);
    else toast.success(`Uploaded ${total} files${roomTag ? ` · ${roomTag}` : ""}`);
    await load();
  };

  const done = job.status === "completed";
  const isFollowUp = job.status === "follow_up";
  const activeOnClock = !!job.clock_in_at && !job.clock_out_at;
  const isPoolJob = !job.assigned_to;
  const isMine = job.assigned_to === user?.id || job.helper_id === user?.id;
  const canWork = isMine || user?.role === "admin";

  const doClaim = async () => {
    try {
      const r = await api.post(`/jobs/${id}/claim`);
      setJob(r.data);
      toast.success("Job claimed! It's yours.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const doRelease = async () => {
    if (releasing) return;
    setReleasing(true);
    try {
      await api.post(`/jobs/${id}/release`);
      toast.success("Released to pool", {
        duration: 10000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post(`/jobs/${id}/claim`);
              toast.success("Claimed back — it's yours again");
              nav(`/worker/jobs/${id}`);
            } catch (e) {
              toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not undo — job may have been claimed by someone else");
            }
          },
        },
      });
      nav("/worker");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message || "Could not release job";
      toast.error(msg);
      setReleasing(false);
    }
  };

  return (
    <WorkerShell>
      <span className="hidden" data-proman-context={`Worker job page. WO ${job.work_order_number || job.id}: "${job.title}" at ${job.property_address}${job.unit ? ` unit ${job.unit}` : ""}. Status ${job.status}. Clocked ${job.clock_in_at && !job.clock_out_at ? "IN" : "out"}. Photos before ${(job.before_photos || []).length} / after ${(job.after_photos || []).length}. Receipts ${(job.receipts || []).length}.${job.return_visit?.date ? ` Returning ${job.return_visit.date}.` : ""}`}/>
      <FollowUpModal
        open={followUpOpen}
        onClose={() => setFollowUpOpen(false)}
        onSubmit={submitFollowUp}
        defaultNotes={job?.work_performed_polished || job?.work_performed || ""}
      />
      <div className="px-4 py-4">
        <button onClick={() => nav("/worker")} className="flex items-center gap-1 text-sm font-bold uppercase text-zinc-600" data-testid="back-btn">
          <ArrowLeft weight="bold" size={16}/> All Jobs
        </button>

        {/* Legal Protocol banner — Fair Housing / Moisture / Mold WOs get an
            auto-attached documentation checklist + moisture reading form so
            evidence collection can't be skipped. */}
        <LegalProtocolPanel jobId={id}/>
        <FundingGuardBanner job={job}/>

        <div className="mt-4 rugged-card p-5">
          <ShowUpCard job={job} onReload={load}/>
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={job.status}/>
            {isFollowUp && <FollowUpBadge reason={job.follow_up_reason}/>}
            {isPoolJob && <span className="px-2 py-0.5 text-xs font-black uppercase tracking-wider text-white bg-orange-500 inline-flex items-center gap-1"><HandGrabbing weight="bold" size={12}/> UNCLAIMED</span>}
            {job.needs_docs && !job.docs_completed_at && (
              <span data-testid="needs-docs-badge" className="px-2 py-0.5 text-xs font-black uppercase tracking-wider text-white bg-amber-500 inline-flex items-center gap-1">
                <Notepad weight="fill" size={12}/> Docs Needed
              </span>
            )}
            <span className="text-xs font-black uppercase text-slate-500">{job.priority}</span>
            {job.assigned_to_name && !isPoolJob && <span className="text-xs font-black uppercase text-slate-500">· {job.assigned_to_name}</span>}
            {job.habitability && (
              <>
                <span data-testid="habitability-badge"
                  title={job.habitability_manual ? "Flagged as habitability by office" : "Auto-detected habitability"}
                  className="inline-flex items-center gap-1 h-5 px-1.5 bg-orange-600 text-white text-[10px] font-black uppercase tracking-widest rounded-md">
                  <FilePdf weight="fill" size={10}/> Habitability · 30-Day Rule
                </span>
                <HabitabilityCountdown startAt={job.created_at} testId="habitability-countdown-worker"/>
              </>
            )}
          </div>
          <h1 className="mt-2 font-display font-black text-3xl tracking-tight">{job.title}</h1>
          {job.work_order_number && (
            <button
              type="button"
              onClick={() => { try { navigator.clipboard.writeText(job.work_order_number); toast.success(`Copied WO#${job.work_order_number}`); } catch (_) {} }}
              data-testid="worker-header-wo"
              className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 bg-[#0f172a] hover:bg-slate-800 text-white text-xs font-black uppercase tracking-widest font-mono cursor-pointer"
              title="Click to copy WO number"
            >
              WO# {job.work_order_number} <span className="opacity-60 text-[9px]">⧉</span>
            </button>
          )}
          <InlineWoFix jobId={job.id || job._id} currentWo={job.work_order_number || ""} onSaved={load} variant="chip"/>
          {job.portfolio_name && (
            <Link to={`/admin/portfolios/${job.portfolio_slug}`} data-testid="jobdetail-portfolio-chip"
              className="mt-2 ml-2 inline-flex items-center px-2.5 py-1 text-white text-xs font-black uppercase tracking-widest"
              style={{ background: job.portfolio_color || "#0f172a" }}>
              {job.portfolio_name}
            </Link>
          )}
          {job.portfolio_access_notes && (
            <div className="mt-2 text-xs font-bold text-amber-900 bg-amber-50 border border-amber-300 px-3 py-1.5 inline-flex items-center gap-1.5" data-testid="jobdetail-access-notes">
              🔑 {job.portfolio_access_notes}
            </div>
          )}
          {(job.merged_from || []).length > 0 && job.status !== "completed" && (
            <div className="mt-2 text-xs font-bold text-fuchsia-900 bg-fuchsia-50 border border-fuchsia-300 px-3 py-1.5 inline-flex items-center gap-1.5"
              data-testid="jobdetail-merge-hint">
              🔗 {(job.merged_from || []).length} consolidated ticket{(job.merged_from || []).length === 1 ? "" : "s"} — will auto-close with this WO
            </div>
          )}
          {job.status === "merged" && job.merged_into && (
            <Link to={`/admin/jobs/${job.merged_into}`}
              className="mt-2 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 px-3 py-1.5 inline-flex items-center gap-1.5"
              data-testid="jobdetail-merged-into-link">
              ↗ Consolidated into master WO — tap to open
            </Link>
          )}
          <div className="mt-2 flex items-center gap-1.5 text-slate-700 font-medium flex-wrap">
            <MapPin weight="bold" size={16}/>
            <Link
              to={`/admin/properties/${encodeURIComponent(job.property_address)}`}
              data-testid="worker-header-address-link"
              className="underline decoration-dotted decoration-slate-400 hover:decoration-solid hover:text-[#00C4CC]"
            >
              {job.property_address}
            </Link>
            {job.unit && <span className="ml-1 px-2 py-0.5 bg-fuchsia-600 text-white font-black uppercase text-[10px] tracking-widest rounded-sm">Apt {formatUnit(job.unit)}</span>}
            {job.entry_permission === "open" && <span className="px-2 py-0.5 bg-emerald-600 text-white font-black uppercase text-[10px] tracking-widest rounded-sm" data-testid="entry-perm-open">✓ Enter Anytime</span>}
            {job.entry_permission === "no_entry" && <span className="px-2 py-0.5 bg-red-600 text-white font-black uppercase text-[10px] tracking-widest rounded-sm" data-testid="entry-perm-no">✕ Do Not Enter</span>}
            {(!job.entry_permission || job.entry_permission === "confirm") && <span className="px-2 py-0.5 bg-amber-500 text-white font-black uppercase text-[10px] tracking-widest rounded-sm" data-testid="entry-perm-confirm">! Confirm Entry</span>}
            {job.en_route_at && <span className="px-2 py-0.5 bg-amber-500 text-white font-black uppercase text-[10px] tracking-widest rounded-sm" data-testid="en-route-badge">🚗 On the way</span>}
          </div>
          {job.customer_name && <div className="mt-1 text-sm text-slate-600">{t("job.customer")}: {job.customer_name} {job.customer_phone && `· ${job.customer_phone}`}</div>}
          {job.property_address && (
            <div className="mt-3">
              <VendorSpendPill address={job.property_address} unit={job.unit || undefined}/>
            </div>
          )}
          <div className="mt-3">
            <JobCoverageBanner jobId={job.id} onClaimFiled={load} onWarrantyAssigned={load}/>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button onClick={openVoicePunch} data-testid="job-voice-punch-btn"
              className="h-10 px-3 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
              🎤 Voice · Add Punch Items to Project
            </button>
            {user?.role === "admin" && (
              <button onClick={() => setShowAiDraft(true)} data-testid="job-ai-draft-update-btn"
                className="h-10 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 shadow-[2px_2px_0_#0f172a]">
                <Sparkle weight="fill" size={12}/> AI · Draft Landlord Update
              </button>
            )}
          </div>
          {voicePunch && (
            <VoiceToPunchModal
              projectId={voicePunch.id || voicePunch._id}
              projectName={voicePunch.name}
              rooms={voicePunch.rooms || []}
              defaultRoom={job.unit || ""}
              onClose={() => setVoicePunch(null)}
              onAdded={() => setVoicePunch(null)}
            />
          )}
          {job.description && <p className="mt-3 text-slate-700 whitespace-pre-wrap">{job.description}</p>}

          {/* Quick Update — one-shot card for the 90% case: drop a photo/file,
              add a note, nudge the status. Avoids the "hunt through tabs" UX. */}
          <QuickUpdateCard job={job} uploadPhoto={uploadPhoto}
            onReload={load} disabled={done || (!canWork && user?.role !== "admin")}/>

          {job.property_address && (
            <div className="mt-4 space-y-3">
              <PropertyNotesCard address={job.property_address} readOnly={true}/>
              <PropertyHistoryPanel address={job.property_address} currentJobId={job.id} adminLink={false}/>
            </div>
          )}
          {(job.entry_code || job.lockbox_code || job.entry_notes) && (
            <div className="mt-4 p-3 border-2 border-dashed border-[#00C4CC] bg-cyan-50" data-testid="entry-info-card">
              <div className="text-xs font-black uppercase tracking-wider text-[#0e7490]">{t("job.entry_access")}</div>
              <div className="mt-1 text-lg font-mono font-black">
                {job.entry_code && <span data-testid="entry-code">{t("job.entry_code")}: {job.entry_code}</span>}
                {job.entry_code && job.lockbox_code && <span className="mx-2 text-slate-400">·</span>}
                {job.lockbox_code && <span data-testid="lockbox-code">{t("job.lockbox")}: {job.lockbox_code}</span>}
              </div>
              {job.entry_notes && <div className="mt-1 text-sm text-slate-700">{job.entry_notes}</div>}
            </div>
          )}

          {/* Field Notes (from ProMan or manual) */}
          {(job.notes || []).length > 0 && (
            <div className="mt-4 p-3 border-2 border-[#0f172a] bg-white" data-testid="field-notes-card">
              <div className="flex items-center gap-1.5 mb-2">
                <Notepad weight="bold" size={16} className="text-[#00C4CC]"/>
                <div className="text-xs font-black uppercase tracking-wider">Field Notes <span className="text-slate-400">· {(job.notes || []).length}</span></div>
              </div>
              <ul className="space-y-2">
                {(job.notes || []).slice().reverse().map((n, i) => (
                  <li key={n.id || i} data-testid={`field-note-${i}`}
                    className="border-l-2 border-[#00C4CC] pl-2.5 py-0.5">
                    <div className="text-sm text-slate-800 whitespace-pre-wrap">{n.text}</div>
                    <div className="mt-0.5 text-[10px] font-black uppercase tracking-widest text-slate-400">
                      {n.by_name || "Field"}{n.source === "agent" ? " · via ProMan" : ""}{n.at ? ` · ${new Date(n.at).toLocaleString()}` : ""}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {job.needs_docs && !job.docs_completed_at && (
            <NeedsDocsBanner job={job} onDone={load}/>
          )}
          <DuplicatesBanner job={job} role={user?.role === "admin" ? "admin" : "worker"} onMerged={load}/>
          <AutoDiagnoseBanner job={job} onReload={load} disabled={done}/>
          <PestTrapGuidePanel job={job}/>
          <KraaftJobPanel jobId={job.id}/>
          <TenantHistoryPanel job={job} role="worker"/>
          {user?.role === "admin" && (
            <TriageLinkCard job={job} onMinted={load}/>
          )}
          {user?.role === "admin" && (
            <div className="mt-4 rugged-card p-3 flex items-center justify-between gap-3 bg-white" data-testid="habitability-toggle-card">
              <div className="min-w-0">
                <div className="text-xs font-black uppercase tracking-wider text-slate-600 flex items-center gap-1">
                  <FilePdf weight="fill" size={12} className={job.habitability ? "text-orange-600" : "text-slate-400"}/>
                  Habitability / Code (Philly Safe Healthy Homes Act)
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {job.habitability ? "This job is flagged as a habitability issue — 30-day repair clock applies." : "Not flagged. Toggle on for code / habitability issues (leaks, no heat, mold, pests, gas)."}
                  {job.habitability_manual && <span className="ml-1 font-black text-orange-700">· LOCKED by office</span>}
                </div>
              </div>
              <button
                data-testid="habitability-toggle-btn"
                onClick={async () => {
                  try {
                    await api.post(`/jobs/${job.id}/toggle-habitability`, { value: !job.habitability });
                    toast.success(job.habitability ? "Habitability flag cleared" : "Flagged as habitability issue");
                    await load();
                  } catch (e) {
                    toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update flag");
                  }
                }}
                className={`shrink-0 tactile-btn h-10 px-4 font-black uppercase tracking-wider text-xs border-2 border-[#0f172a] ${job.habitability ? "bg-orange-600 text-white hover:bg-orange-700" : "bg-white text-[#0f172a] hover:bg-orange-50"}`}>
                {job.habitability ? "ON" : "OFF"}
              </button>
              {(job.habitability || user?.role === "admin") && (
                <a
                  href={`${API}/jobs/${job.id}/compliance-pdf?auth=${encodeURIComponent(localStorage.getItem("access_token") || "")}`}
                  target="_blank" rel="noreferrer"
                  data-testid="compliance-pdf-btn"
                  className="shrink-0 tactile-btn h-10 px-4 font-black uppercase tracking-wider text-xs border-2 border-orange-700 bg-orange-600 text-white hover:bg-orange-700 inline-flex items-center gap-1.5"
                  title="Export Philly Safe Healthy Homes Act compliance packet">
                  <FilePdf weight="fill" size={14}/> PDF
                </a>
              )}
            </div>
          )}
          {/* Location gate toggle — admin can flip OFF the "clock in from
              on-site only" rule for a stuck tech (bad signal, gated complex).
              OFF means: no GPS requirement for clock-in on THIS job only. */}
          {user?.role === "admin" && (
            <div className="mt-4 rugged-card p-3 flex items-center justify-between gap-3 bg-white" data-testid="location-gate-card">
              <div className="min-w-0">
                <div className="text-xs font-black uppercase tracking-wider text-slate-600 flex items-center gap-1">
                  <MapPin weight="fill" size={12} className={job.location_gate_disabled ? "text-red-500" : "text-emerald-600"}/>
                  Location Gate — clock-in
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {job.location_gate_disabled
                    ? <>Location check <b>DISABLED</b> for this WO — tech can clock in without GPS.{" "}
                      {job.location_gate_disabled_by && <span>Off by <b>{job.location_gate_disabled_by}</b></span>}</>
                    : "Tech must have GPS on and be within ~500 m of the property to clock in."}
                </div>
              </div>
              <button
                data-testid="location-gate-toggle-btn"
                onClick={async () => {
                  try {
                    await api.post(`/jobs/${job.id}/toggle-location-gate`, { disabled: !job.location_gate_disabled });
                    toast.success(job.location_gate_disabled ? "Location gate ON" : "Location gate OFF for this WO");
                    await load();
                  } catch (e) {
                    toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not toggle");
                  }
                }}
                className={`shrink-0 tactile-btn h-10 px-4 font-black uppercase tracking-wider text-xs border-2 border-[#0f172a] ${job.location_gate_disabled ? "bg-red-500 text-white hover:bg-red-600" : "bg-emerald-500 text-white hover:bg-emerald-600"}`}>
                {job.location_gate_disabled ? "OFF" : "ON"}
              </button>
            </div>
          )}
        </div>

        {/* CLAIM / RELEASE */}
        {isPoolJob && user?.role === "worker" && (
          <button onClick={doClaim} data-testid="claim-job-btn"
            className="tactile-btn mt-5 w-full h-20 bg-orange-500 hover:bg-orange-600 text-white font-display font-black text-2xl uppercase tracking-wide flex items-center justify-center gap-3">
            <HandGrabbing weight="fill" size={28}/> {t("job.claim")}
          </button>
        )}
        {isMine && !done && (
          <button onClick={doRelease} data-testid="release-job-btn" disabled={releasing}
            className="mt-3 w-full h-11 border-2 border-red-500 text-red-600 font-black uppercase text-xs tracking-wider hover:bg-red-500 hover:text-white transition-colors bg-white disabled:opacity-50">
            {releasing ? "Releasing…" : "Release Back to Pool"}
          </button>
        )}

        {/* ARRIVAL BANNER — GPS-based, appears when worker is on-site and not clocked in */}
        {arrived && !arrivalDismissed && !activeOnClock && !done && canWork && !isPoolJob && (
          <div className="mt-3 rugged-card p-3 bg-emerald-50 border-l-4 border-emerald-600 flex items-center gap-3" data-testid="arrival-banner">
            <MapPin weight="fill" size={22} className="text-emerald-700 flex-shrink-0"/>
            <div className="flex-1 min-w-0">
              <div className="font-display font-black text-sm uppercase tracking-wider text-emerald-900">
                You've arrived
              </div>
              <div className="text-xs text-emerald-800 font-medium truncate">Tap to clock in for this job.</div>
            </div>
            <button onClick={async () => { await doClockIn(); setArrived(false); }}
              data-testid="arrival-clock-in-btn"
              className="tactile-btn h-10 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-xs tracking-wider inline-flex items-center gap-1 flex-shrink-0">
              <Play weight="fill" size={14}/> Clock In
            </button>
            <button onClick={() => setArrivalDismissed(true)}
              data-testid="arrival-dismiss-btn"
              className="h-8 w-8 border-2 border-emerald-600 text-emerald-700 font-black flex items-center justify-center flex-shrink-0">
              ×
            </button>
          </div>
        )}

        {/* DIRECTIONS BUTTON — opens maps AND clocks in (when eligible) in the same tap so travel time gets paid.
            No confirm dialog: the "+ Clock In" badge is the explicit disclosure. */}
        {job.property_address && !done && (
          <button onClick={(e) => {
              const dest = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(job.property_address)}&travelmode=driving`;
              // Open maps FIRST while we still have the user-gesture (mobile popup blockers require this)
              window.open(dest, "_blank", "noopener,noreferrer");
              // Then clock in the background if this worker owns the job and isn't already on the clock
              if (canWork && !isPoolJob && isMine && !activeOnClock) {
                doClockIn();
              }
            }}
            data-testid="directions-btn"
            className="tactile-btn mt-3 w-full h-14 bg-blue-600 hover:bg-blue-700 text-white font-display font-black text-lg uppercase tracking-wide flex items-center justify-center gap-2">
            <MapPin weight="fill" size={22}/> Get Directions
            {canWork && !isPoolJob && isMine && !activeOnClock && (
              <span className="ml-2 text-[10px] font-black uppercase tracking-widest bg-white/20 px-1.5 py-0.5">+ Clock In</span>
            )}
          </button>
        )}

        {/* ON MY WAY — ping office with a one-tap ETA chip. No more prompt() —
             techs on the road need to slam a button, not type a number. */}
        {canWork && !isPoolJob && isMine && !done && !job.en_route_at && (
          <div className="mt-3" data-testid="en-route-quick">
            <div className="grid grid-cols-4 gap-1.5">
              {[5, 10, 15, 30].map((mins) => (
                <button
                  key={mins}
                  data-testid={`en-route-eta-${mins}`}
                  onClick={async () => {
                    try {
                      const r = await api.post(`/jobs/${id}/en-route`, { eta_minutes: mins });
                      toast.success(r.data.message || "Office notified");
                      startLocationWatch();
                      await load();
                    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Ping failed"); }
                  }}
                  className="tactile-btn h-14 bg-amber-500 hover:bg-amber-600 text-white font-black uppercase text-xs tracking-wider flex flex-col items-center justify-center leading-tight border-2 border-[#0f172a]"
                >
                  <span className="text-lg">{mins}m</span>
                  <span className="text-[9px] opacity-90">ETA</span>
                </button>
              ))}
            </div>
            <div className="mt-1 text-[10px] font-black uppercase tracking-widest text-slate-500 text-center inline-flex items-center gap-1 justify-center w-full">
              <MapPin weight="bold" size={11}/> Tap = tell office you're rolling
            </div>
          </div>
        )}
        {canWork && !isPoolJob && isMine && !done && job.en_route_at && (
          <button
            onClick={async () => {
              const eta = prompt("Update ETA in minutes:", "10");
              if (eta === null) return;
              const etaN = parseInt(eta, 10);
              try {
                const r = await api.post(`/jobs/${id}/en-route`, { eta_minutes: Number.isFinite(etaN) ? etaN : null });
                toast.success(r.data.message || "Office notified");
                startLocationWatch();
                await load();
              } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Ping failed"); }
            }}
            data-testid="en-route-btn"
            className="tactile-btn mt-3 w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-white font-black uppercase text-sm tracking-wider flex items-center justify-center gap-2">
            <MapPin weight="bold" size={16}/> Update ETA — Office Notified ✓
          </button>
        )}

        {/* LUNCH — one tap in, one tap out. No dialog, no counter, just a
             big banner while they eat so the Complete button doesn't tempt
             a mid-bite close. */}
        {canWork && !isPoolJob && isMine && !done && activeOnClock && !job.lunch_start_at && (
          <button
            onClick={async () => {
              try {
                const r = await api.post(`/jobs/${id}/lunch-start`);
                setJob(r.data);
                toast.success("On lunch — enjoy");
              } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start lunch"); }
            }}
            data-testid="lunch-start-btn"
            className="tactile-btn mt-3 w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-black uppercase text-sm tracking-wider flex items-center justify-center gap-2 border-2 border-[#0f172a]"
          >
            🍔 Take Lunch
          </button>
        )}
        {job.lunch_start_at && (
          <div className="mt-3 p-3 bg-orange-100 border-2 border-orange-500 rounded-xl flex items-center gap-3" data-testid="lunch-active-banner">
            <div className="text-3xl">🍔</div>
            <div className="flex-1 min-w-0">
              <div className="font-black uppercase tracking-wider text-orange-900 text-sm">On lunch</div>
              <div className="text-[11px] text-orange-800 font-bold">
                Started {new Date(job.lunch_start_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
              </div>
            </div>
            <button
              onClick={async () => {
                try {
                  const r = await api.post(`/jobs/${id}/lunch-end`);
                  setJob(r.data);
                  const last = (r.data.lunch_breaks || []).slice(-1)[0];
                  toast.success(last ? `Back on the clock — ${last.minutes} min lunch` : "Back on the clock");
                } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not end lunch"); }
              }}
              data-testid="lunch-end-btn"
              className="tactile-btn h-11 px-4 bg-orange-600 hover:bg-orange-700 text-white font-black uppercase text-xs tracking-wider border-2 border-[#0f172a]"
            >
              End Lunch
            </button>
          </div>
        )}

        {/* CAN'T GET IN — one-tap access request */}
        {canWork && !isPoolJob && isMine && !done && (
          <AccessRequestButton jobId={id} openAR={openAR} onSent={loadOpenAR}/>
        )}

        {/* FIELD ↔ OFFICE MESSAGES */}
        {isMine && !isPoolJob && (
          <JobMessages jobId={id} isAdmin={false}/>
        )}

        {/* CLOCK ACTION */}
        {!done && !isPoolJob && canWork && (
          <div className="mt-5">
            {isFollowUp && !activeOnClock && (
              <div className="mb-3 rugged-card p-3 bg-yellow-50 border-yellow-500 flex items-center gap-2">
                <Warning weight="fill" size={20} className="text-yellow-600"/>
                <div className="text-sm font-bold">{t("job.followup_msg")}</div>
              </div>
            )}
            {!activeOnClock ? (
              <button onClick={doClockIn} disabled={busy}
                className="tactile-btn w-full h-20 bg-[#00C4CC] hover:bg-[#00a8af] text-white font-display font-black text-2xl uppercase tracking-wide flex items-center justify-center gap-3"
                data-testid="clock-in-btn">
                <Play weight="fill" size={28}/> {isFollowUp ? t("job.resume_work") : t("job.clock_in")}
              </button>
            ) : (
              <>
                <div className="rugged-card p-4 bg-[#ecfeff]">
                  <div className="flex items-center gap-2 text-[#0e7490] font-black uppercase text-sm tracking-wider">
                    <Clock weight="bold" size={16}/> {t("job.on_clock_since")} {new Date(job.clock_in_at).toLocaleTimeString()}
                  </div>
                  {job.clock_in_location && <div className="mt-1 text-xs text-zinc-600">{t("job.location")}: {job.clock_in_location.lat?.toFixed(4)}, {job.clock_in_location.lng?.toFixed(4)}</div>}
                </div>
                <ClockedInReceiptButton job={job} onReload={load}/>
                <NearbyJobsNudgeWorker jobId={job.id} propertyAddress={job.property_address}/>
              </>
            )}
          </div>
        )}

        {/* CREW & TIME ENTRIES */}
        {(job.assigned_to_name || job.helper_name || (job.time_entries || []).length > 0) && (
          <div className="mt-4 rugged-card p-4" data-testid="crew-panel">
            <div className="text-xs font-black uppercase tracking-wider text-slate-500">{t("job.crew")}</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {job.assigned_to_name && (
                <span className="px-2.5 py-1 border-2 border-[#0f172a] bg-white text-xs font-black uppercase tracking-wider" data-testid="crew-lead-chip">
                  {t("job.lead")}: {job.assigned_to_name}
                </span>
              )}
              {job.helper_name && (
                <span className="px-2.5 py-1 border-2 border-[#00C4CC] bg-[#ecfeff] text-[#0e7490] text-xs font-black uppercase tracking-wider" data-testid="crew-helper-chip">
                  {t("job.helper")}: {job.helper_name}
                </span>
              )}
            </div>
            {(job.time_entries || []).length > 0 && (
              <div className="mt-3 space-y-1">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Time Entries (per-person)</div>
                {job.time_entries.map((e, i) => {
                  const cin = e.clock_in_at && new Date(e.clock_in_at);
                  const cout = e.clock_out_at && new Date(e.clock_out_at);
                  const hrs = cin && cout ? ((cout - cin) / 3600000).toFixed(2) : null;
                  return (
                    <div key={i} className="text-xs flex items-center justify-between border-t border-slate-200 pt-1" data-testid={`time-entry-${i}`}>
                      <span className="font-bold">{e.user_name || "Unknown"}</span>
                      <span className="font-mono text-slate-500">
                        {cin ? cin.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "?"}
                        {" → "}
                        {cout ? cout.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : <span className="text-emerald-600">{t("job.on_clock")}</span>}
                        {hrs && <span className="ml-2 font-black text-[#0f172a]">{hrs}h</span>}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
            {(job.material_runs || []).length > 0 && (
              <div className="mt-3 space-y-1" data-testid="material-runs-panel">
                <div className="text-[10px] font-black uppercase tracking-widest text-orange-600 flex items-center gap-1">
                  <Package weight="bold" size={12}/> {t("job.material_runs")}
                </div>
                {(job.material_runs || []).map((mr, i) => (
                  <div key={mr.id || i} className="text-xs flex items-center justify-between border-t border-slate-200 pt-1" data-testid={`material-run-${i}`}>
                    <span className="font-bold text-slate-700">{mr.user_name || "Worker"}</span>
                    <span className="font-mono text-slate-500">
                      {mr.at ? new Date(mr.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "?"}
                      <span className="ml-2 text-[10px] font-black uppercase tracking-widest text-orange-600">
                        {mr.kind === "return" ? t("job.material_run_return") : t("job.material_run_start")}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Reports bar — always visible so you can download/email the WO report
            without waiting for a "completed" flip. Hidden only if there's zero content. */}
        {!isPoolJob && ((job.before_photos || []).length > 0 || (job.after_photos || []).length > 0 || done) && (
          <ClickUpAttachBanner job={job}/>
        )}
        {!isPoolJob && ((job.before_photos || []).length > 0 || (job.after_photos || []).length > 0 || done) && (
          <div className="mt-4 p-3 bg-white border-2 border-[#0f172a] shadow-[3px_3px_0_#0f172a] flex flex-wrap items-center gap-2" data-testid="jobdetail-reports-bar">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mr-1 flex items-center gap-1">
              <FilePdf weight="bold" size={12}/> Reports
            </div>
            <a href={`${API}/jobs/${job.id}/pdf?customer=true&auth=${encodeURIComponent(localStorage.getItem("access_token") || "")}`}
              target="_blank" rel="noreferrer"
              data-testid="jobdetail-download-pdf-btn"
              className="tactile-btn h-10 px-3 bg-white border-2 border-[#0f172a] text-[#0f172a] hover:bg-slate-50 font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1.5">
              <FilePdf weight="fill" size={14}/> Download PDF
            </a>
            {(job.is_turn || ["move_out","move_in","periodic"].includes(String(job.inspection_type||"").toLowerCase())) && (
              <a href={`${API}/jobs/${job.id}/turn-report-pdf?auth=${encodeURIComponent(localStorage.getItem("access_token") || "")}`}
                target="_blank" rel="noreferrer"
                data-testid="jobdetail-download-turn-pdf-btn"
                className="tactile-btn h-10 px-3 bg-amber-500 hover:bg-amber-600 text-[#0f172a] border-2 border-[#0f172a] font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1.5">
                📝 Sweep Report
              </a>
            )}
            {user?.role === "admin" && (
              <a href={`${API}/jobs/${job.id}/evidence-bundle?auth=${encodeURIComponent(localStorage.getItem("access_token") || "")}`}
                target="_blank" rel="noreferrer"
                data-testid="jobdetail-evidence-bundle-btn"
                title="Untouched original photos + manifest for security-hold defense"
                className="tactile-btn h-10 px-3 bg-slate-800 hover:bg-slate-900 text-white border-2 border-[#0f172a] font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1.5">
                🔒 Evidence Zip
              </a>
            )}
            {user?.role === "admin" && (
              <button onClick={async () => {
                const to = window.prompt("Email this report to (any address — no landlord required):", "");
                if (to === null) return;
                if (!to.trim()) { toast.error("Enter an email"); return; }
                try {
                  const r = await api.post(`/jobs/${job.id}/email-landlord-pdf`, { to_email: to.trim() });
                  toast.success(`Emailed to ${r.data.to}`);
                } catch (e) {
                  toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
                }
              }}
                data-testid="jobdetail-email-report-btn"
                className="tactile-btn h-10 px-3 bg-emerald-600 hover:bg-emerald-700 text-white border-2 border-[#0f172a] font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1.5">
                📧 Email Report
              </button>
            )}
            {!done && (
              <span className="ml-1 text-[10px] font-black uppercase tracking-widest text-slate-400">· preview (WO not marked complete)</span>
            )}
          </div>
        )}

        {/* TABS */}
        {!isPoolJob && (
        <div className="mt-6 flex gap-2 border-b-2 border-[#0f172a] overflow-x-auto" role="tablist">
          {[
            ["work", t("job.tab.work"), <Camera weight="bold" size={16}/>],
            ["issues", t("job.tab.issues"), <ListChecks weight="bold" size={16}/>],
            ["materials", t("job.tab.materials"), <Package weight="bold" size={16}/>],
            ["appliances", t("job.tab.appliances"), <Barcode weight="bold" size={16}/>],
            ...(job.is_turn ? [["chargeback", "Move-Out $", <Warning weight="bold" size={16}/>]] : []),
            ["history", "History", <Clock weight="bold" size={16}/>],
            ["ai", t("job.tab.ai"), <ChatCircle weight="bold" size={16}/>],
          ].map(([k, label, icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
              className={`px-4 py-2 font-black uppercase text-sm tracking-wider flex items-center gap-1.5 whitespace-nowrap ${
                tab === k ? "bg-[#0f172a] text-white" : "text-slate-600"
              }`}>
              {icon}{label}
              {k === "issues" && (job.checklist || []).length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-[#00C4CC] text-white rounded">
                  {(job.checklist || []).filter(c => c.done).length}/{(job.checklist || []).length}
                </span>
              )}
              {k === "appliances" && (job.appliances || []).length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-[#00C4CC] text-white rounded">
                  {(job.appliances || []).length}
                </span>
              )}
              {k === "chargeback" && (() => {
                const all = [...(job.before_photos || []), ...(job.after_photos || []), ...(job.receipts || [])];
                const n = all.filter(p => p.chargeable_to_tenant).length;
                return n > 0 ? <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-red-600 text-white rounded" data-testid="tab-chargeback-count">{n}</span> : null;
              })()}
            </button>
          ))}
        </div>
        )}

        {tab === "work" && !isPoolJob && canWork && (
          <div className="mt-5 space-y-6">
            {job.is_turn && (
              <div className="rugged-card p-4 bg-amber-50 border-l-4 border-amber-500" data-testid="turn-inspection-launcher">
                <div className="font-display font-black text-sm uppercase tracking-wider text-amber-800">
                  Turn Inspections
                </div>
                <div className="mt-1 text-xs text-slate-600 font-medium">
                  Walk room-by-room. Rate condition, snap photos, mark complete.
                </div>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <button onClick={() => setInspectionPhase("move_out")} data-testid="start-move-out-btn"
                    disabled={done || (job.rooms || []).length === 0}
                    className="tactile-btn h-14 bg-red-600 hover:bg-red-700 text-white font-display font-black uppercase text-sm tracking-wide inline-flex items-center justify-center gap-2 disabled:opacity-50">
                    <Play weight="fill" size={18}/> Start Move-Out Inspection
                  </button>
                  <button onClick={() => setInspectionPhase("move_in")} data-testid="start-move-in-btn"
                    disabled={done || (job.rooms || []).length === 0}
                    className="tactile-btn h-14 bg-emerald-600 hover:bg-emerald-700 text-white font-display font-black uppercase text-sm tracking-wide inline-flex items-center justify-center gap-2 disabled:opacity-50">
                    <Play weight="fill" size={18}/> Start Move-In Inspection
                  </button>
                </div>
                {(job.rooms || []).length === 0 && (
                  <div className="mt-2 text-xs text-red-600 font-bold">
                    ⚠ No rooms configured on this Turn job — edit the job to add rooms first.
                  </div>
                )}
              </div>
            )}
            {!done && (
              <button onClick={() => setBatchImport("before")} data-testid="wo-import-batch"
                className="w-full h-12 rounded-xl bg-[#0b1c2d] text-white font-black text-sm uppercase tracking-widest flex items-center justify-center gap-2">
                <Images weight="fill" size={20} className="text-teal-400"/> Import photo batch from gallery
              </button>
            )}
            <PhotoZone kind="before" title={t("job.before")} job={job} onUpload={(f) => uploadPhotos([f], "before")} onUploadMany={(files) => uploadPhotos(files, "before")} onAnnotate={(p) => setAnnotate(p)} onReload={load} disabled={done}/>
            <PhotoZone kind="after" title={t("job.after")} job={job} onUpload={(f) => uploadPhotos([f], "after")} onUploadMany={(files) => uploadPhotos(files, "after")} onAnnotate={(p) => setAnnotate(p)} onReload={load} disabled={done}/>

            {/* One-tap AI Triage + Request-tenant-photos block */}
            <div className="rugged-card p-4 bg-slate-50" data-testid="ai-triage-block">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                    AI Dispatch Triage
                  </div>
                  <div className="text-[12px] text-slate-700 mt-0.5">
                    {((job.before_photos || []).length + (job.after_photos || []).length) === 0
                      ? "No photos yet — request some from the tenant, or run triage from title/description only."
                      : "Read all attached photos + description to auto-categorise, urgency-flag, and extract appliance data-plate."}
                  </div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <AITriageButton jobId={job.id} triage={job.ai_triage || null} onTriaged={load}
                  onRequestPhotos={(reason) => setRequestPhotos({ reason })}/>
                <button onClick={() => setRequestPhotos({ reason: "" })} data-testid="req-photos-open"
                  className="h-10 px-3 border-2 border-slate-900 bg-white font-black uppercase tracking-widest text-[11px] inline-flex items-center gap-1">
                  <Camera weight="bold" size={12}/> Request Tenant Photos
                </button>
              </div>
            </div>
            {requestPhotos && (
              <RequestPhotosModal jobId={job.id}
                defaultMessage={requestPhotos.reason || ""}
                defaultEmail={job.customer_email || job.tenant_email || ""}
                onClose={() => setRequestPhotos(null)} onCreated={load}/>
            )}

            <WorkPerformedSection job={job} onReload={load} disabled={done}/>

            {!done && activeOnClock && (
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => doComplete("completed")} data-testid="complete-btn"
                  className="tactile-btn h-16 bg-green-600 hover:bg-green-700 text-white font-display font-black uppercase flex items-center justify-center gap-2">
                  <CheckCircle weight="fill" size={22}/> Complete
                </button>
                <button onClick={() => doComplete("follow_up")} data-testid="follow-up-btn"
                  className="tactile-btn h-16 bg-yellow-500 hover:bg-yellow-600 text-[#09090b] font-display font-black uppercase flex items-center justify-center gap-2">
                  <Warning weight="fill" size={22}/> Follow-up
                </button>
              </div>
            )}
            {!done && activeOnClock && (
              <button onClick={doClockOut} className="w-full h-12 border-2 border-[#09090b] font-black uppercase text-sm tracking-wider" data-testid="clock-out-btn">
                <Stop weight="bold" size={14} className="inline mr-1"/> {t("job.clock_out")}
              </button>
            )}
            {!done && !isPoolJob && (
              job.return_visit?.date ? (
                <div className="border-2 border-orange-500 bg-orange-50 p-3 flex items-start gap-2" data-testid="return-visit-banner">
                  <ArrowUUpLeft weight="bold" size={18} className="text-orange-600 mt-0.5"/>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-black uppercase tracking-widest text-orange-800">Not finished — returning {fmtShort(job.return_visit.date)}</div>
                    {job.return_visit.note && <div className="text-sm text-orange-900 mt-0.5">{job.return_visit.note}</div>}
                  </div>
                  <button onClick={async () => { await api.post(`/jobs/${job.id}/return-later`, { clear: true }); load(); }} data-testid="return-visit-clear"
                    className="text-[10px] font-black uppercase text-orange-700 underline">Clear</button>
                </div>
              ) : returnOpen ? (
                <ReturnLaterPanel jobId={job.id} onClose={() => setReturnOpen(false)} onDone={load}/>
              ) : (
                <button onClick={() => setReturnOpen(true)} data-testid="return-later-btn"
                  className="w-full h-12 border-2 border-orange-500 text-orange-700 bg-white font-black uppercase text-sm tracking-wider inline-flex items-center justify-center gap-2">
                  <ArrowUUpLeft weight="bold" size={16}/> Not finished — return another day
                </button>
              )
            )}
            {done && (
              <div className="rugged-card p-5 bg-[#f0fdf4]">
                <div className="font-black uppercase text-sm tracking-wider text-green-700 flex items-center gap-1.5">
                  <CheckCircle weight="fill" size={18}/> {t("job.completed")}
                </div>
                {job.completion_notes && <p className="mt-2 text-zinc-700 whitespace-pre-wrap">{job.completion_notes}</p>}
                <div className="mt-4 flex flex-wrap gap-2">
                  <a
                    href={`${API}/jobs/${job.id}/pdf?auth=${encodeURIComponent(localStorage.getItem("access_token") || "")}`}
                    target="_blank" rel="noreferrer"
                    data-testid="download-pdf-btn"
                    className="tactile-btn h-12 px-5 bg-[#00C4CC] text-white font-black uppercase text-sm tracking-wider inline-flex items-center gap-2">
                    <FilePdf weight="fill" size={16}/> Internal PDF
                  </a>
                  <a
                    href={`${API}/jobs/${job.id}/pdf?customer=true&auth=${encodeURIComponent(localStorage.getItem("access_token") || "")}`}
                    target="_blank" rel="noreferrer"
                    data-testid="download-customer-pdf-btn"
                    className="tactile-btn h-12 px-5 bg-white text-[#0f172a] border-2 border-[#0f172a] font-black uppercase text-sm tracking-wider inline-flex items-center gap-2 hover:bg-slate-50">
                    <FilePdf weight="fill" size={16}/> Customer PDF
                  </a>
                  {user?.role === "admin" && (
                    <button
                      onClick={async () => {
                        const to = window.prompt("Send this WO's landlord PDF to (leave blank to use landlord on file):", "");
                        try {
                          const r = await api.post(`/jobs/${job.id}/email-landlord-pdf`, { to_email: (to||"").trim() });
                          toast.success(`Emailed to ${r.data.to}`);
                        } catch (e) {
                          toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
                        }
                      }}
                      data-testid="email-landlord-pdf-btn"
                      className="tactile-btn h-12 px-5 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-sm tracking-wider inline-flex items-center gap-2 border-2 border-[#0f172a]">
                      <FilePdf weight="fill" size={16}/> Email to Landlord
                    </button>
                  )}
                  {job.is_turn && (
                    <a
                      href={`${API}/jobs/${job.id}/turn-report-pdf?auth=${encodeURIComponent(localStorage.getItem("access_token") || "")}`}
                      target="_blank" rel="noreferrer"
                      data-testid="download-turn-report-btn"
                      className="tactile-btn h-12 px-5 bg-amber-500 hover:bg-amber-600 text-white font-black uppercase text-sm tracking-wider inline-flex items-center gap-2">
                      <FilePdf weight="fill" size={16}/> Turn Report
                    </a>
                  )}
                  {user?.role === "admin" && (
                    <a
                      href={`${API}/jobs/${job.id}/compliance-pdf?auth=${encodeURIComponent(localStorage.getItem("access_token") || "")}`}
                      target="_blank" rel="noreferrer"
                      data-testid="download-compliance-pdf-btn"
                      title="Philadelphia Safe Healthy Homes Act compliance packet"
                      className="tactile-btn h-12 px-5 bg-orange-600 hover:bg-orange-700 text-white font-black uppercase text-sm tracking-wider inline-flex items-center gap-2">
                      <FilePdf weight="fill" size={16}/> Compliance PDF
                    </a>
                  )}
                </div>
              </div>
            )}
            {done && (
              <SignatureCard job={job} onReload={load} onOpen={() => setShowSig(true)}/>
            )}
          </div>
        )}

        {isPoolJob && user?.role === "worker" && (
          <div className="mt-6 rugged-card p-6 bg-orange-50 text-center" data-testid="pool-info-card">
            <HandGrabbing weight="bold" size={40} className="mx-auto text-orange-600"/>
            <div className="mt-3 font-display font-black text-xl">{t("job.pool_title")}</div>
            <p className="mt-1 text-sm text-slate-600 font-medium">{t("job.pool_sub")}</p>
          </div>
        )}

        {tab === "issues" && !isPoolJob && <ChecklistPanel job={job} onReload={load} disabled={done || !canWork}/>}
        {tab === "materials" && !isPoolJob && <MaterialsPanel job={job} onReload={load} uploadPhoto={uploadPhoto} disabled={done || !canWork}/>}
        {tab === "appliances" && !isPoolJob && <AppliancesPanel job={job} onReload={load} disabled={done || !canWork}/>}
        {tab === "chargeback" && !isPoolJob && job.is_turn && (
          <ChargebackPanel jobId={job.id} isAdmin={user?.role === "admin"} onJobReload={load}/>
        )}
        {tab === "ai" && !isPoolJob && <AIPanel jobId={job.id} appliance={job.title} disabled={done || !canWork}/>}
        {tab === "history" && !isPoolJob && (
          <div className="mt-5">
            <JobHistoryTimeline jobId={job.id}/>
          </div>
        )}
      </div>

      {annotate && (
        <PhotoAnnotator
          imagePath={annotate.path}
          onClose={() => setAnnotate(null)}
          onSave={async (file) => {
            await uploadPhoto(file, "annotated");
            setAnnotate(null);
          }}
        />
      )}

      {showSig && (
        <SignaturePad
          defaultName={job.customer_name || ""}
          onCancel={() => setShowSig(false)}
          onSave={async ({ dataUrl, signerName }) => {
            try {
              await api.post(`/jobs/${job.id}/signature`, { image_data: dataUrl, signer_name: signerName });
              toast.success("Signature saved");
              setShowSig(false);
              await load();
            } catch (e) {
              toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save signature");
            }
          }}
        />
      )}

      {inspectionPhase && (
        (() => {
          const savedMode = (typeof localStorage !== "undefined" && localStorage.getItem("insp_flow_mode")) || "simple";
          if (savedMode === "classic") {
            return (
              <InspectionWizard
                jobId={job.id}
                phase={inspectionPhase}
                onClose={() => setInspectionPhase(null)}
                onJobReload={load}
              />
            );
          }
          return (
            <InspectionFlowSimple
              jobId={job.id}
              phase={inspectionPhase}
              onClose={() => setInspectionPhase(null)}
              onJobReload={load}
              onSwitchClassic={() => {
                try { localStorage.setItem("insp_flow_mode", "classic"); } catch {}
                window.location.reload();
              }}
            />
          );
        })()
      )}

      {/* GEOFENCE EXIT MODAL — appears when clocked-in worker has drifted > 250m from the property */}
      {exitPrompt && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-end sm:items-center justify-center p-4" data-testid="exit-prompt-modal">
          <div className="w-full max-w-md rugged-card p-5 bg-white border-2 border-[#0f172a]">
            <div className="flex items-center gap-2 text-[#0f172a]">
              <MapPin weight="fill" size={22} className="text-orange-500"/>
              <div className="font-display font-black text-xl uppercase tracking-tight">
                {t("job.exit.title")}
              </div>
            </div>
            <div className="mt-2 text-sm text-slate-700 font-medium">
              {t("job.exit.body")}
            </div>
            <div className="mt-4 space-y-2">
              <button onClick={async () => { setExitPrompt(false); await doClockOut(); }} disabled={busy}
                data-testid="exit-clock-out-btn"
                className="tactile-btn w-full h-14 bg-red-500 hover:bg-red-600 text-white font-display font-black text-base uppercase tracking-wider flex items-center justify-center gap-2">
                <Stop weight="fill" size={18}/> {t("job.exit.clock_out")}
              </button>
              <button onClick={doMaterialRun} disabled={busy}
                data-testid="exit-material-run-btn"
                className="tactile-btn w-full h-14 bg-[#00C4CC] hover:bg-[#00a8af] text-white font-display font-black text-base uppercase tracking-wider flex items-center justify-center gap-2">
                <Package weight="fill" size={18}/> {t("job.exit.material_run")}
              </button>
              <button onClick={dismissExitPrompt}
                data-testid="exit-dismiss-btn"
                className="w-full h-11 border-2 border-slate-300 text-slate-600 font-black uppercase text-xs tracking-wider hover:bg-slate-50">
                {t("job.exit.dismiss")}
              </button>
            </div>
            <div className="mt-3 text-[11px] text-slate-500 font-medium leading-snug">
              {t("job.exit.footer")}
            </div>
          </div>
        </div>
      )}
      {roomPicker && (
        <RoomTagPickerModal
          open
          rooms={job?.rooms || []}
          count={roomPicker.files?.length || 0}
          kind={roomPicker.kind}
          onCancel={handleRoomCancel}
          onPick={handleRoomPick}
        />
      )}
      {batchImport && (
        <BatchPhotoImport title={`Import ${batchImport} photos`} subtitle={`${job.property_address || ""} · WO ${job.work_order_number || ""}`}
          upload={async (file) => {
            const fd = new FormData();
            fd.append("kind", batchImport);
            fd.append("file", file, file.name);
            await api.post(`/jobs/${job.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          }}
          onClose={() => setBatchImport(null)}
          onDone={async (n) => { setBatchImport(null); toast.success(`${n} ${batchImport} photos added`); await load(); }}
          extra={(
            <div className="px-4 pt-3 flex gap-2" data-testid="wo-batch-kind">
              {["before", "after"].map(k => (
                <button key={k} onClick={() => setBatchImport(k)} data-testid={`wo-batch-kind-${k}`}
                  className={`flex-1 h-10 rounded-xl font-black text-xs uppercase tracking-widest ${batchImport === k ? "bg-teal-500 text-slate-900" : "bg-white/10 text-slate-300"}`}>{k} photos</button>
              ))}
            </div>
          )}/>
      )}
      {/* Bulk Photo Tray — visible only when there are untagged photos AND a checklist */}
      {(job?.checklist?.length || 0) > 0 && (
        <div className="px-4 max-w-3xl mx-auto">
          <BulkPhotoTray job={job} onReload={load} baseUrl={BASE_URL_FOR_TRAY} />
        </div>
      )}

      {/* Flag as Bulk Trash · Truck Day — always available on any WO with an address */}
      {job?.property_address && (
        <div className="px-4 max-w-3xl mx-auto mt-3 space-y-2">
          <ApplianceIQChip address={job.property_address}/>
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              data-testid="job-sync-clickup-btn"
              onClick={async () => {
                try {
                  const r = await api.post(`/clickup/jobs/${job.id || job._id}/sync-now`);
                  toast.success(`ClickUp: ${r.data.cu_status} · FR: ${r.data.fr_status}`);
                  load();
                } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "No ClickUp task found")); }
              }}
              className="h-9 px-3 border-2 border-cyan-500 bg-cyan-50 hover:bg-cyan-100 text-cyan-900 font-black uppercase tracking-widest text-[10px] rounded-md flex items-center gap-1">
              🔄 Sync from ClickUp
            </button>
            <button
              type="button"
              data-testid="job-dedupe-desc-btn"
              onClick={async () => {
                if (!window.confirm("Collapse duplicate 'Merged from…' blocks in the description?")) return;
                try {
                  const r = await api.post(`/clickup/jobs/${job.id || job._id}/deduplicate-description`);
                  toast.success(r.data.changed ? `Deduped: ${r.data.old_length}→${r.data.new_length} chars` : "Nothing to dedupe");
                  if (r.data.changed) load();
                } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Dedupe failed")); }
              }}
              className="h-9 px-3 border-2 border-slate-400 bg-white hover:bg-slate-50 text-slate-700 font-black uppercase tracking-widest text-[10px] rounded-md flex items-center gap-1">
              🧹 Dedupe Description
            </button>
          </div>
          <button
            type="button"
            data-testid="job-flag-purge-btn"
            onClick={() => setPurgeOpen(true)}
            className="w-full h-11 border-2 border-amber-500 bg-amber-50 hover:bg-amber-100 text-amber-900 font-black uppercase tracking-widest text-xs rounded-md flex items-center justify-center gap-2">
            🚛 Flag Bulk Trash Here · Truck Day
          </button>
        </div>
      )}
      <PurgeFlagModal
        open={purgeOpen}
        onClose={() => setPurgeOpen(false)}
        defaultAddress={job?.property_address || ""}
        jobId={job?.id || null}
      />
      {/* Send Review Request — one-tap tenant review link (available on any job with a customer email) */}
      {job?.status === "completed" && (
        <div className="px-4 max-w-3xl mx-auto mt-4">
          <button
            type="button"
            data-testid="send-review-request-btn"
            onClick={async () => {
              const email = window.prompt("Send review request to:", job.customer_email || "");
              if (!email) return;
              try {
                const r = await api.post(`/jobs/${id}/review-request`, { to_email: email });
                toast.success(r.data?.sent ? "Review link sent" : "Queued (email may be dropped)");
              } catch (e) {
                toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send");
              }
            }}
            className="w-full h-12 bg-amber-500 text-white font-black uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2 hover:opacity-90 border-2 border-[#0f172a]"
          >
            ⭐ Send Review Request To Tenant
          </button>
        </div>
      )}
      {/* Reopen Job — undo a mis-tapped Complete/Follow-up/Cancel any time (admin or assigned tech) */}
      {job && ["completed", "follow_up", "canceled"].includes(job.status) && (user?.role === "admin" || isMine) && (
        <div className="px-4 max-w-3xl mx-auto mt-3">
          <button
            type="button"
            data-testid="reopen-job-btn"
            onClick={async () => {
              if (!window.confirm("Reopen this job? Status goes back to In Progress and clears the completion.")) return;
              try {
                const r = await api.post(`/jobs/${id}/reopen`);
                setJob(r.data);
                toast.success("Reopened — back to work");
              } catch (e) {
                toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not reopen");
              }
            }}
            className="w-full h-12 bg-slate-700 text-white font-black uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2 hover:bg-slate-800 border-2 border-[#0f172a]"
          >
            ↺ Reopen Job
          </button>
        </div>
      )}
      {/* Share Live ETA — DoorDash-style tenant map link */}
      {job && ["scheduled","in_progress","on_site","dispatched"].includes(job.status) && (
        <div className="px-4 max-w-3xl mx-auto mt-3">
          <button
            type="button"
            data-testid="share-eta-btn"
            onClick={async () => {
              const email = window.prompt("Send Live ETA link to (email):", job.customer_email || "");
              if (email === null) return;
              try {
                const r = await api.post(`/jobs/${id}/eta-share`, { channel: email ? "email" : "manual", to: email });
                if (email) toast.success("Live ETA link sent");
                if (r.data?.link) { try { await navigator.clipboard.writeText(r.data.link); toast.success("Link also copied to clipboard"); } catch {} }
                // Start posting tech location every 30s (browser geolocation)
                if (navigator.geolocation) {
                  const watch = navigator.geolocation.watchPosition(async (pos) => {
                    try {
                      await api.patch(`/jobs/${id}/tech-location`, {
                        lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy,
                      });
                    } catch {}
                  }, () => {}, { enableHighAccuracy: true, maximumAge: 15000 });
                  window._etaWatch = watch;
                  toast.success("Sharing your location — tenant will see it live");
                }
              } catch (e) {
                toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not share");
              }
            }}
            className="w-full h-12 bg-sky-600 text-white font-black uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2 hover:opacity-90 border-2 border-[#0f172a]"
          >
            🚚 Share Live ETA With Tenant
          </button>
        </div>
      )}
      <DraftUpdateModal
        open={showAiDraft}
        jobId={job?.id}
        onClose={() => setShowAiDraft(false)}
        onSent={() => setShowAiDraft(false)}
      />
    </WorkerShell>
  );
}


function StatusBadge({ status }) {
  const map = {
    assigned: ["Assigned", "bg-blue-500"],
    in_progress: ["In Progress", "bg-[#00C4CC]"],
    completed: ["Completed", "bg-green-600"],
    follow_up: ["Follow-up", "bg-yellow-500"],
  };
  const [label, color] = map[status] || map.assigned;
  return <span className={`px-2 py-0.5 text-xs font-black uppercase tracking-wider text-white ${color}`}>{label}</span>;
}


/** Colored pill for a follow-up sub-reason (paint touch-up / storm verify / other). */
export function FollowUpBadge({ reason }) {
  if (!reason) return null;
  const cfg = {
    paint_touch_up: { label: "Paint Touch-Up", icon: <PaintBrush weight="fill" size={12}/>, bg: "bg-fuchsia-600" },
    roof_storm_verify: { label: "Storm/Rain Verify", icon: <CloudRain weight="fill" size={12}/>, bg: "bg-sky-600" },
    other: { label: "Follow-Up · Other", icon: <Warning weight="fill" size={12}/>, bg: "bg-yellow-500" },
  }[reason];
  if (!cfg) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 h-5 px-1.5 text-white text-[10px] font-black uppercase tracking-widest ${cfg.bg}`}
      data-testid={`follow-up-badge-${reason}`}
      title={cfg.label}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

/** Big-button modal Andrew's crew taps to pick why a follow-up is needed. */
function FollowUpModal({ open, onClose, onSubmit, defaultNotes = "" }) {
  const [reason, setReason] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setReason(null); setNotes(""); } }, [open]);
  if (!open) return null;
  const pick = (r) => {
    setReason(r);
    // "Other" needs a note; presets fill their own suggested note if none typed
    if (r !== "other" && !notes.trim()) {
      const suggested = {
        paint_touch_up: "Repair + spackle done — revisit to paint match.",
        roof_storm_verify: "Roof/window repair done — revisit after next storm to confirm it held.",
      }[r];
      if (suggested) setNotes(suggested);
    }
  };
  const submit = async () => {
    if (!reason) return toast.error("Pick a reason first");
    if (reason === "other" && !notes.trim()) return toast.error("Type the follow-up detail");
    setBusy(true);
    try {
      const combinedNotes = defaultNotes
        ? defaultNotes + (notes ? `\n\nFollow-up: ${notes}` : "")
        : notes;
      await onSubmit({ reason, notes: combinedNotes });
      onClose();
    } finally { setBusy(false); }
  };
  const Btn = ({ r, label, icon, bg }) => {
    const active = reason === r;
    return (
      <button
        onClick={() => pick(r)}
        data-testid={`follow-up-pick-${r}`}
        className={`tactile-btn h-24 border-4 font-display font-black uppercase text-sm tracking-widest flex flex-col items-center justify-center gap-1.5 transition-all ${
          active ? `${bg} text-white border-[#0f172a] scale-[1.02]` : "bg-white text-[#0f172a] border-slate-300 hover:border-[#0f172a]"
        }`}>
        <span className="text-2xl">{icon}</span>
        {label}
      </button>
    );
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="follow-up-modal">
      <div className="w-full sm:max-w-md rugged-card bg-white p-5 sm:p-6 max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-black text-xl uppercase tracking-tight">Why a follow-up?</h2>
          <button onClick={onClose} className="p-1" data-testid="follow-up-close"><X weight="bold" size={22}/></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Btn r="paint_touch_up"    label="Paint Touch-Up"    icon={<PaintBrush weight="fill" size={26}/>}      bg="bg-fuchsia-600"/>
          <Btn r="roof_storm_verify" label={<>Storm/Rain<br/>Verify</>} icon={<CloudRain weight="fill" size={26}/>} bg="bg-sky-600"/>
        </div>
        <button
          onClick={() => pick("other")}
          data-testid="follow-up-pick-other"
          className={`tactile-btn mt-3 w-full h-12 border-4 font-black uppercase text-xs tracking-widest flex items-center justify-center gap-2 ${
            reason === "other" ? "bg-yellow-500 text-[#0f172a] border-[#0f172a]" : "bg-white border-slate-300 hover:border-[#0f172a] text-[#0f172a]"
          }`}>
          <Warning weight="fill" size={16}/> Other reason
        </button>
        <label className="mt-4 block">
          <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Detail</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            data-testid="follow-up-notes"
            placeholder="What still needs to happen? When should we come back?"
            className="mt-1 w-full border-2 border-[#0f172a] p-2 text-sm font-medium bg-white focus:outline-none focus:ring-4 focus:ring-[#00C4CC]/30"
          />
        </label>
        <div className="mt-4 flex gap-2">
          <button onClick={onClose} data-testid="follow-up-cancel"
            className="flex-1 h-12 border-2 border-[#0f172a] bg-white font-black uppercase text-xs tracking-widest">
            Cancel
          </button>
          <button onClick={submit} disabled={busy || !reason} data-testid="follow-up-submit"
            className="flex-[2] h-12 bg-yellow-500 hover:bg-yellow-600 text-[#0f172a] font-black uppercase text-sm tracking-widest disabled:opacity-60 inline-flex items-center justify-center gap-2">
            <Warning weight="fill" size={16}/> {busy ? "Saving…" : "Mark Follow-Up"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SignatureCard({ job, onReload, onOpen }) {
  const { t } = useT();
  const sig = job.signature;
  const clear = async () => {
    if (!window.confirm(t("sig.confirm_remove"))) return;
    try {
      await api.delete(`/jobs/${job.id}/signature`);
      toast.success(t("sig.removed"));
      await onReload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not remove signature");
    }
  };
  if (sig?.path) {
    const signed = sig.signed_at ? new Date(sig.signed_at).toLocaleString() : "";
    return (
      <div className="rugged-card p-5 bg-white" data-testid="signature-card-signed">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Signature weight="bold" size={20} className="text-[#00C4CC]"/>
            <h3 className="font-display font-black text-lg uppercase">{t("sig.card_title")}</h3>
          </div>
          <button onClick={clear} data-testid="signature-clear-saved-btn"
            className="h-9 px-3 border-2 border-red-500 text-red-600 font-black uppercase text-xs tracking-wider hover:bg-red-500 hover:text-white transition-colors">
            {t("sig.redo")}
          </button>
        </div>
        <div className="mt-3 border-2 border-[#0f172a] bg-white p-3 flex items-center justify-center">
          <img src={fileUrl(sig.path)} alt="Customer signature" className="max-h-32 object-contain" data-testid="signature-image"/>
        </div>
        <div className="mt-3 text-sm font-medium">
          <div><span className="font-black uppercase text-xs tracking-wider text-slate-500">{t("sig.signed_by")}:</span> <b>{sig.signer_name}</b></div>
          <div className="text-xs text-slate-500 mt-0.5">{signed}</div>
        </div>
      </div>
    );
  }
  return (
    <button onClick={onOpen} data-testid="open-signature-btn"
      className="tactile-btn w-full h-16 border-2 border-dashed border-[#0f172a] bg-white hover:bg-slate-50 font-display font-black uppercase tracking-wide flex items-center justify-center gap-2">
      <PencilSimple weight="bold" size={22}/> {t("job.get_signature")}
    </button>
  );
}

function PhotoZone({ kind, title, job, onUpload, onUploadMany, disabled, onAnnotate, onReload }) {
  const { t } = useT();
  const { user } = useAuth();
  const ref = useRef();
  const videoRef = useRef();
  const [tagFor, setTagFor] = useState(null);
  const [chargeFor, setChargeFor] = useState(null);
  const [customTags, setCustomTags] = useState([]);
  const [aiTagBusy, setAiTagBusy] = useState(false);
  const [viewer, setViewer] = useState(null);
  useEffect(() => {
    api.get("/settings").then(r => setCustomTags(r.data.custom_tags || [])).catch(() => {});
  }, []);
  const photos = kind === "before" ? job.before_photos : job.after_photos;
  const DEFAULT_TAGS = ["Before", "During", "After Repair", "Damage Detail", "Serial/Model", "Access Point", "Safety Concern", "Materials Used"];
  const TAGS = [...DEFAULT_TAGS, ...customTags];
  const toggleTag = async (photo, tag) => {
    const current = photo.tags || [];
    const next = current.includes(tag) ? current.filter(t => t !== tag) : [...current, tag];
    try {
      await api.patch(`/jobs/${job.id}/photo/${photo.file_id}/tags`, { tags: next });
      photo.tags = next; // optimistic local update
      setTagFor({ ...photo });
    } catch (_) { toast.error("Could not save tag"); }
  };
  const showChargeToggle = !!job.is_turn && !disabled;

  const aiTagAll = async () => {
    const targets = photos.filter(p => {
      if (p.ai_tags && p.ai_tags.length) return false;
      const existing = p.tags || [];
      const defaultOnly = existing.length === 0 || (existing.length === 1 && (existing[0] === "Before" || existing[0] === "After Repair" || existing[0] === "Marked"));
      return defaultOnly && (p.media_type !== "video");
    });
    if (targets.length === 0) {
      toast.info("All photos already tagged");
      return;
    }
    setAiTagBusy(true);
    const tid = toast.loading(`AI tagging ${targets.length} photo${targets.length === 1 ? "" : "s"}…`);
    try {
      const r = await api.post(`/jobs/${job.id}/photos/ai-tag-batch`, { file_ids: targets.map(p => p.file_id) });
      toast.dismiss(tid);
      toast.success(`AI tagged ${r.data.tagged}/${targets.length}`);
      if (onReload) await onReload();
    } catch (e) {
      toast.dismiss(tid);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI tagging failed");
    } finally { setAiTagBusy(false); }
  };
  return (
    <div>
      <div className="flex items-center justify-between">
        <h3 className="font-display font-black text-lg uppercase tracking-wide">{title}</h3>
        <div className="flex items-center gap-2">
          {!disabled && photos.length > 0 && (
            <button onClick={aiTagAll} disabled={aiTagBusy}
              data-testid={`ai-tag-all-${kind}`}
              className="h-8 px-2 border-2 border-violet-600 bg-violet-50 hover:bg-violet-100 text-violet-800 font-black uppercase text-[10px] tracking-wider inline-flex items-center gap-1 disabled:opacity-50">
              <Sparkle weight="fill" size={12}/> {aiTagBusy ? "Tagging…" : "AI Tag All"}
            </button>
          )}
          <span className="text-xs font-bold text-slate-500">{photos.length} items</span>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-3">
        {photos.map((p, i) => {
          const isVideo = p.media_type === "video" || (p.content_type || "").startsWith("video/");
          return (
            <div key={i} className="relative aspect-square border-2 border-[#0f172a] overflow-hidden bg-slate-100 group">
              <button type="button" onClick={() => setViewer(p)} className="block w-full h-full" data-testid="photo-open">
                {isVideo ? (
                  <div className="relative w-full h-full">
                    <video src={mediaSrc(p)} className="w-full h-full object-cover" muted playsInline preload="metadata"/>
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <div className="h-10 w-10 bg-white rounded-full flex items-center justify-center">
                        <Play weight="fill" size={22} className="text-[#0f172a] ml-0.5"/>
                      </div>
                    </div>
                  </div>
                ) : (
                  <img src={mediaSrc(p)} alt="" className="w-full h-full object-cover"/>
                )}
              </button>
              {p.annotated && (
                <span className="absolute top-1 left-1 px-1.5 py-0.5 text-[10px] font-black uppercase bg-[#00C4CC] text-white">Marked</span>
              )}
              {p.source === "clickup" && p.clickup_task_id && (
                <a href={`https://app.clickup.com/t/${p.clickup_task_id}`}
                  target="_blank" rel="noreferrer"
                  onClick={e => e.stopPropagation()}
                  title={`Auto-attached from ClickUp${p.filename ? " · " + p.filename : ""}${p.uploaded_at ? " · " + new Date(p.uploaded_at).toLocaleDateString() : ""}`}
                  data-testid={`clickup-badge-${kind}-${i}`}
                  className={`absolute ${p.annotated ? "top-6" : "top-1"} left-1 px-1.5 py-0.5 text-[10px] font-black uppercase bg-fuchsia-600 hover:bg-fuchsia-700 text-white inline-flex items-center gap-1`}>
                  <ClipboardText weight="fill" size={10}/> ClickUp
                </a>
              )}
              {(p.ai_tags || []).length > 0 && (
                <span className="absolute top-1 right-1 px-1.5 py-0.5 text-[10px] font-black uppercase bg-violet-600 text-white" title={p.ai_note || "AI tagged"} data-testid="ai-tag-badge">AI</span>
              )}
              {(p.tags || []).length > 0 && (
                <div className="absolute bottom-1 left-1 right-10 flex flex-wrap gap-0.5">
                  {(p.tags || []).slice(0, 2).map(t => (
                    <span key={t} className="px-1 py-0.5 text-[9px] font-black uppercase bg-[#0f172a] text-white">{t}</span>
                  ))}
                  {(p.tags || []).length > 2 && <span className="px-1 py-0.5 text-[9px] font-black uppercase bg-[#0f172a] text-white">+{(p.tags || []).length - 2}</span>}
                </div>
              )}
              {!disabled && (
                <button onClick={() => setTagFor(p)} data-testid={`tag-${kind}-${i}`}
                  title={t("photo.tag_title")}
                  className="absolute top-1 right-1 h-7 w-7 bg-white border-2 border-[#0f172a] flex items-center justify-center opacity-80 hover:opacity-100 text-xs font-black">
                  #
                </button>
              )}
              {p.chargeable_to_tenant && (
                <div className="absolute inset-0 pointer-events-none ring-4 ring-red-600"></div>
              )}
              {p.chargeable_to_tenant && (
                <span className="absolute top-1 left-1 px-1.5 py-0.5 text-[9px] font-black uppercase bg-red-600 text-white tracking-wider" data-testid={`charge-badge-${kind}-${i}`}>
                  Tenant $
                </span>
              )}
              {!disabled && !isVideo && onAnnotate && (
                <button onClick={() => onAnnotate(p)} data-testid={`annotate-${kind}-${i}`}
                  title="Mark up photo"
                  className="absolute bottom-1 right-1 h-8 w-8 bg-white border-2 border-[#0f172a] flex items-center justify-center opacity-80 hover:opacity-100">
                  <PaintBrush weight="bold" size={16}/>
                </button>
              )}
              {showChargeToggle && (
                <button onClick={() => setChargeFor(p)} data-testid={`charge-${kind}-${i}`}
                  title="Flag as tenant-chargeable damage"
                  className={`absolute bottom-1 left-1 h-8 px-2 border-2 border-[#0f172a] flex items-center justify-center text-[10px] font-black uppercase tracking-wider ${p.chargeable_to_tenant ? "bg-red-600 text-white" : "bg-white text-[#0f172a]"}`}>
                  {p.chargeable_to_tenant ? "$ FLAGGED" : "$ FLAG"}
                </button>
              )}
            </div>
          );
        })}
        {!disabled && (
          <>
            <button onClick={() => ref.current?.click()} data-testid={`add-${kind}-photo`}
              className="aspect-square dashed-drop flex flex-col items-center justify-center text-[#0f172a] font-black uppercase text-xs tracking-wider">
              <Camera weight="bold" size={28}/>
              <span className="mt-1">Photo</span>
            </button>
            <button onClick={() => videoRef.current?.click()} data-testid={`add-${kind}-video`}
              className="aspect-square dashed-drop flex flex-col items-center justify-center text-[#0f172a] font-black uppercase text-xs tracking-wider">
              <VideoCamera weight="bold" size={28}/>
              <span className="mt-1">Video</span>
            </button>
          </>
        )}
      </div>
      <input ref={ref} type="file" accept="image/*" multiple className="hidden"
        onChange={(e) => { if (e.target.files?.length) { onUploadMany ? onUploadMany(e.target.files) : onUpload(e.target.files[0]); e.target.value = ""; }}}/>
      <input ref={videoRef} type="file" accept="video/*" multiple className="hidden"
        onChange={(e) => { if (e.target.files?.length) { onUploadMany ? onUploadMany(e.target.files) : onUpload(e.target.files[0]); e.target.value = ""; }}}/>

      {viewer && (
        <div className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setViewer(null)} data-testid="photo-viewer">
          {(viewer.media_type === "video" || (viewer.content_type || "").startsWith("video/")) ? (
            <video src={mediaSrc(viewer)} controls autoPlay playsInline
              className="max-w-full max-h-full" onClick={e => e.stopPropagation()}/>
          ) : (
            <img src={mediaSrc(viewer)} alt=""
              className="max-w-full max-h-full object-contain" onClick={e => e.stopPropagation()}/>
          )}
          <button type="button" onClick={() => setViewer(null)} aria-label="Close"
            className="absolute top-4 right-4 h-10 w-10 bg-white border-2 border-[#0f172a] font-black text-lg leading-none">x</button>
        </div>
      )}

      {tagFor && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setTagFor(null)}>
          <div className="w-full max-w-sm bg-white border-2 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] p-5" onClick={e => e.stopPropagation()} data-testid="tag-picker">
            <h3 className="font-display font-black text-xl">{t("photo.tag_photo")}</h3>
            <p className="mt-1 text-xs text-slate-500 font-bold uppercase">{t("photo.tap_toggle")}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {TAGS.map(t => {
                const on = (tagFor.tags || []).includes(t);
                return (
                  <button key={t} onClick={() => toggleTag(tagFor, t)} data-testid={`tag-opt-${t}`}
                    className={`px-3 py-2 border-2 border-[#0f172a] text-xs font-black uppercase tracking-wider ${on ? "bg-[#00C4CC] text-white" : "bg-white"}`}>
                    {t}
                  </button>
                );
              })}
            </div>
            <button onClick={() => setTagFor(null)} data-testid="tag-picker-done"
              className="tactile-btn mt-5 w-full h-11 bg-[#0f172a] text-white font-black uppercase text-sm tracking-wider">
              {t("photo.done")}
            </button>
          </div>
        </div>
      )}

      {chargeFor && (
        <ChargeableModal
          photo={chargeFor}
          jobId={job.id}
          isAdmin={user?.role === "admin"}
          onClose={() => setChargeFor(null)}
          onSaved={async () => { setChargeFor(null); if (onReload) await onReload(); }}
        />
      )}
    </div>
  );
}

function ChargeableModal({ photo, jobId, isAdmin, onClose, onSaved }) {
  const [flagged, setFlagged] = useState(!!photo.chargeable_to_tenant);
  const [desc, setDesc] = useState(photo.damage_description || "");
  const [amount, setAmount] = useState(photo.office_deduction_amount ? String(photo.office_deduction_amount) : "");
  const [officeNotes, setOfficeNotes] = useState(photo.office_notes || "");
  const [reviewed, setReviewed] = useState(!!photo.office_reviewed);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        chargeable_to_tenant: flagged,
        damage_description: desc.trim(),
      };
      if (isAdmin) {
        payload.office_reviewed = reviewed;
        payload.office_deduction_amount = parseFloat(amount) || 0;
        payload.office_notes = officeNotes.trim();
      }
      await api.patch(`/jobs/${jobId}/photo/${photo.file_id}/chargeable`, payload);
      toast.success(flagged ? "Tenant damage saved" : "Flag cleared");
      await onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-white border-2 border-red-600 shadow-[6px_6px_0px_#dc2626] p-5 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()} data-testid="chargeable-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-black text-xl uppercase text-red-700">Tenant Damage</h3>
          <button onClick={onClose} className="h-8 w-8 border-2 border-[#0f172a] font-black">×</button>
        </div>
        <p className="mt-1 text-xs text-slate-500 font-bold uppercase">Move-out chargeback evidence</p>

        <div className="mt-3 border-2 border-[#0f172a] p-2">
          <img src={fileUrl(photo.path)} alt="damage" className="w-full h-40 object-cover"/>
        </div>

        <label className="mt-4 flex items-center gap-2 cursor-pointer" data-testid="charge-flag-toggle">
          <input type="checkbox" checked={flagged} onChange={e => setFlagged(e.target.checked)} className="h-5 w-5"/>
          <span className="font-black uppercase text-sm tracking-wider">Tenant responsible for damage</span>
        </label>

        <div className="mt-3">
          <label className="text-xs font-black uppercase tracking-wider text-slate-600">Damage Description</label>
          <textarea value={desc} onChange={e => setDesc(e.target.value)} data-testid="charge-desc-input"
            placeholder="e.g. Large hole in living room wall, ~6in, from what appears to be a doorknob impact."
            className="mt-1 w-full border-2 border-[#0f172a] p-2 text-sm min-h-[80px]"/>
        </div>

        {isAdmin && (
          <div className="mt-4 border-t-2 border-dashed border-slate-300 pt-3">
            <div className="text-xs font-black uppercase tracking-wider text-slate-500">Office Review (Admin only)</div>
            <div className="mt-2">
              <label className="text-xs font-black uppercase tracking-wider text-slate-600">Deduction Amount ($)</label>
              <input type="number" step="0.01" min="0" value={amount} onChange={e => setAmount(e.target.value)}
                data-testid="charge-amount-input" placeholder="0.00"
                className="mt-1 w-full border-2 border-[#0f172a] p-2 text-sm font-mono"/>
            </div>
            <div className="mt-2">
              <label className="text-xs font-black uppercase tracking-wider text-slate-600">Office Notes</label>
              <textarea value={officeNotes} onChange={e => setOfficeNotes(e.target.value)} data-testid="charge-office-notes"
                placeholder="Internal notes, quote source, etc."
                className="mt-1 w-full border-2 border-[#0f172a] p-2 text-sm min-h-[60px]"/>
            </div>
            <label className="mt-2 flex items-center gap-2 cursor-pointer" data-testid="charge-reviewed-toggle">
              <input type="checkbox" checked={reviewed} onChange={e => setReviewed(e.target.checked)} className="h-5 w-5"/>
              <span className="font-black uppercase text-sm tracking-wider">Mark as reviewed</span>
            </label>
          </div>
        )}

        <div className="mt-5 grid grid-cols-2 gap-3">
          <button onClick={onClose} data-testid="charge-cancel-btn"
            className="h-11 border-2 border-[#0f172a] font-black uppercase text-sm tracking-wider">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="charge-save-btn"
            className="tactile-btn h-11 bg-red-600 hover:bg-red-700 text-white font-black uppercase text-sm tracking-wider">
            {busy ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// "You're right here — grab one more?" nudge on the Worker's clocked-in job
function NearbyJobsNudgeWorker({ jobId }) {
  const [state, setState] = useState({ loading: true, jobs: [] });
  const [claiming, setClaiming] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/jobs/${jobId}/nearby`).then(r => {
      if (alive) setState({ loading: false, jobs: (r.data?.jobs || []).filter(j => j.status === "pool") });
    }).catch(() => { if (alive) setState({ loading: false, jobs: [] }); });
    return () => { alive = false; };
  }, [jobId]);
  const claim = async (id) => {
    setClaiming(id);
    try {
      await api.post(`/jobs/${id}/claim`);
      toast.success("Grabbed — one less trip tomorrow");
      setState(s => ({ ...s, jobs: s.jobs.filter(j => j.id !== id) }));
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not claim")); }
    finally { setClaiming(null); }
  };
  if (state.loading || state.jobs.length === 0) return null;
  return (
    <div className="rugged-card p-3 bg-cyan-50 border-[#00C4CC]" data-testid="nearby-nudge-worker">
      <div className="flex items-center gap-2 flex-wrap">
        <Sparkle weight="fill" size={16} className="text-[#0e7490]"/>
        <span className="font-display font-black text-sm uppercase tracking-wide">You're already here</span>
        <span className="text-[10px] font-black uppercase tracking-widest bg-[#0f172a] text-white px-1.5 py-0.5">
          {state.jobs.length} nearby open
        </span>
      </div>
      <p className="text-[11px] text-slate-600 font-medium mt-1">Grab one more while you're on-site — save the trip.</p>
      <div className="mt-2 space-y-1.5">
        {state.jobs.slice(0, 3).map(j => (
          <div key={j.id} className="flex items-center gap-2 bg-white p-2 border-2 border-[#0f172a]">
            <div className="flex-1 min-w-0">
              <div className="text-xs font-black uppercase truncate">{j.title}</div>
              <div className="text-[10px] text-slate-500 truncate">{j.property_address}{j.unit ? ` · #${j.unit}` : ""}</div>
            </div>
            <button onClick={() => claim(j.id)} disabled={claiming === j.id}
              data-testid={`nudge-claim-${j.id}`}
              className="tactile-btn h-8 px-3 bg-[#00C4CC] hover:bg-[#00a8af] text-white font-black uppercase text-[10px] tracking-widest disabled:opacity-60">
              {claiming === j.id ? "…" : "Grab"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ClockedInReceiptButton({ job, onReload }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const scan = async (file) => {
    if (!file) return;
    setBusy(true);
    const tt = toast.loading("Scanning receipt with AI…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/jobs/${job.id}/scan-receipt`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.added > 0) toast.success(`${data.added} item${data.added === 1 ? "" : "s"} added from receipt`, { id: tt });
      else if (data.parsed) toast.warning("Receipt read but no items detected — try a clearer photo", { id: tt });
      else toast.warning("AI couldn't read this receipt cleanly — try again", { id: tt });
      onReload && onReload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Receipt scan failed", { id: tt });
    } finally { setBusy(false); }
  };
  return (
    <>
      <button
        onClick={() => ref.current?.click()}
        disabled={busy}
        data-testid="clocked-in-scan-receipt"
        className="tactile-btn mt-3 w-full h-16 bg-white border-2 border-[#0f172a] text-[#0f172a] font-display font-black text-lg uppercase tracking-wide flex items-center justify-center gap-2 hover:bg-[#f8fafc] disabled:opacity-60">
        {busy ? (<><Sparkle weight="fill" size={22} className="animate-pulse text-[#00C4CC]"/> Reading receipt…</>)
              : (<><Receipt weight="bold" size={22}/> Scan Receipt (Depot / Lowes)</>)}
      </button>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" capture="environment"
        data-testid="clocked-in-scan-receipt-input"
        className="hidden"
        onChange={(e) => { if (e.target.files?.[0]) { scan(e.target.files[0]); e.target.value = ""; } }}/>
    </>
  );
}

function MaterialsPanel({ job, onReload, uploadPhoto, disabled }) {
  const { t } = useT();
  const [name, setName] = useState("");
  const [qty, setQty] = useState("1");
  const [cost, setCost] = useState("0");
  const [store, setStore] = useState("Home Depot");
  const receiptRef = useRef();
  const scanReceiptRef = useRef();
  const [scanning, setScanning] = useState(false);
  const [stockOpen, setStockOpen] = useState(false);

  const scanReceipt = async (file) => {
    if (!file) return;
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/jobs/${job.id}/scan-receipt`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.added > 0) toast.success(`${data.added} item${data.added === 1 ? "" : "s"} added from receipt`);
      else if (data.parsed) toast.warning("Receipt read but no items detected — try a clearer photo");
      else toast.warning("AI couldn't read this receipt cleanly — add items manually");
      await onReload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Receipt scan failed"); }
    finally { setScanning(false); }
  };

  const add = async () => {
    if (!name.trim()) return toast.error(t("mat.name_required"));
    try {
      await api.post(`/jobs/${job.id}/materials`, {
        name: name.trim(), quantity: parseFloat(qty) || 1, unit_cost: parseFloat(cost) || 0, store
      });
      setName(""); setQty("1"); setCost("0");
      onReload();
      toast.success("Material added");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (mid) => {
    await api.delete(`/jobs/${job.id}/materials/${mid}`);
    onReload();
  };

  const total = (job.materials || []).reduce((s, m) => s + (m.quantity || 0) * (m.unit_cost || 0), 0);

  return (
    <div className="mt-5 space-y-6">
      {!disabled && (
      <div className="rugged-card p-4">
        <h3 className="font-display font-black text-lg uppercase">{t("mat.add_material")}</h3>
        <button
          onClick={() => scanReceiptRef.current?.click()}
          disabled={scanning}
          data-testid="scan-receipt-btn"
          className="tactile-btn mt-3 w-full h-14 bg-[#00C4CC] text-white font-black uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {scanning ? (<><Sparkle weight="fill" size={20} className="animate-pulse"/> {t("mat.reading_receipt")}</>)
                    : (<><Receipt weight="bold" size={20}/> {t("mat.scan_receipt")}</>)}
        </button>
        <button
          onClick={() => setStockOpen(true)}
          data-testid="use-stock-btn"
          className="tactile-btn mt-2 w-full h-12 bg-[#0f172a] text-white font-black uppercase text-sm tracking-wider inline-flex items-center justify-center gap-2"
        >
          <Package weight="bold" size={18}/> {t("mat.truck_stock")}
        </button>
        <input ref={scanReceiptRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
          data-testid="scan-receipt-input"
          onChange={(e) => { if (e.target.files?.[0]) { scanReceipt(e.target.files[0]); e.target.value = ""; } }}/>
        <div className="mt-4 pt-4 border-t-2 border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-400 text-center">Or enter manually</div>
        <div className="mt-3 space-y-3">
          <input value={name} onChange={e => setName(e.target.value)} placeholder={'Item name (e.g., PVC Elbow 1/2")'}
            data-testid="material-name" className="w-full h-12 px-3 border-2 border-[#09090b] font-medium"/>
          <div className="grid grid-cols-3 gap-2">
            <input value={qty} onChange={e => setQty(e.target.value)} type="number" step="0.1" placeholder="Qty"
              data-testid="material-qty" className="h-12 px-3 border-2 border-[#09090b] font-medium"/>
            <input value={cost} onChange={e => setCost(e.target.value)} type="number" step="0.01" placeholder="Unit $"
              data-testid="material-cost" className="h-12 px-3 border-2 border-[#09090b] font-medium"/>
            <select value={store} onChange={e => setStore(e.target.value)} data-testid="material-store"
              className="h-12 px-2 border-2 border-[#09090b] font-medium bg-white">
              <option>Home Depot</option><option>Lowe's</option><option>Ace</option><option>Other</option>
            </select>
          </div>
          <button onClick={add} data-testid="add-material-btn"
            className="tactile-btn w-full h-12 bg-[#09090b] text-white font-black uppercase tracking-wider">
            <Plus weight="bold" size={16} className="inline mr-1"/> {t("mat.add_item")}
          </button>
        </div>
      </div>
      )}

      <div>
        <div className="flex items-center justify-between">
          <h3 className="font-display font-black text-lg uppercase">Items ({(job.materials || []).length})</h3>
          <span className="font-display font-black text-xl">${total.toFixed(2)}</span>
        </div>
        <div className="mt-3 space-y-2">
          {(job.materials || []).map(m => (
            <div key={m.id} className="rugged-card p-3 flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate">{m.name}</div>
                <div className="text-xs text-zinc-500">{m.quantity} × ${(m.unit_cost || 0).toFixed(2)} · {m.store}</div>
              </div>
              <div className="font-black">${((m.quantity || 0) * (m.unit_cost || 0)).toFixed(2)}</div>
              {!disabled && <button onClick={() => del(m.id)} data-testid={`del-material-${m.id}`} className="ml-2 p-2 text-red-600"><Trash weight="bold" size={16}/></button>}
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 id="receipts-section" className="font-display font-black text-lg uppercase">Receipts ({(job.receipts || []).length})</h3>
        <div className={`mt-2 p-3 border-2 text-xs ${job.no_purchase ? "border-emerald-500 bg-emerald-50" : (job.receipts || []).length ? "border-slate-200" : "border-amber-400 bg-amber-50"}`} data-testid="receipt-gate-box">
          {(job.receipts || []).length === 0 && !job.no_purchase && (
            <div className="font-bold text-amber-900 mb-2">🧾 Receipt required before this job can be closed. Upload it above, or tick “No purchase” below.</div>
          )}
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={!!job.no_purchase} data-testid="no-purchase-checkbox"
              onChange={async (e) => {
                try {
                  if (!e.target.checked) { await api.post(`/jobs/${job.id}/no-purchase`, { clear: true }); onReload?.(); return; }
                  const reason = window.prompt("No purchase on this job — why? (e.g. labor only, used truck stock)", "Labor only");
                  if (!reason) return;
                  await api.post(`/jobs/${job.id}/no-purchase`, { reason });
                  toast.success("Noted — no purchase on this job"); onReload?.();
                } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail ?? "Could not save")); }
              }} className="mt-0.5 w-4 h-4 accent-emerald-600"/>
            <span><b>No purchase on this job</b>{job.no_purchase ? ` — ${job.no_purchase.reason} (${job.no_purchase.by})` : " — nothing was bought; no receipt to add"}</span>
          </label>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-3">
          {(job.receipts || []).map((r, i) => (
            <PhotoOpen key={i} photo={r} className="aspect-square border-2 border-[#09090b] overflow-hidden relative">
              <img src={mediaSrc(r)} alt="" className="w-full h-full object-cover"/>
              {r.source === "clickup" && r.clickup_task_id && (
                <a href={`https://app.clickup.com/t/${r.clickup_task_id}`}
                  target="_blank" rel="noreferrer"
                  onClick={e => e.stopPropagation()}
                  title={`Auto-attached from ClickUp${r.filename ? " · " + r.filename : ""}${r.uploaded_at ? " · " + new Date(r.uploaded_at).toLocaleDateString() : ""}`}
                  data-testid={`clickup-badge-receipt-${i}`}
                  className="absolute top-1 left-1 px-1.5 py-0.5 text-[10px] font-black uppercase bg-fuchsia-600 hover:bg-fuchsia-700 text-white inline-flex items-center gap-1">
                  <ClipboardText weight="fill" size={10}/> ClickUp
                </a>
              )}
            </PhotoOpen>
          ))}
          {!disabled && (
          <button onClick={() => receiptRef.current?.click()} data-testid="add-receipt-btn"
            className="aspect-square dashed-drop flex flex-col items-center justify-center font-black uppercase text-xs">
            <Receipt weight="bold" size={28}/><span className="mt-1">{t("mat.add_receipt")}</span>
          </button>
          )}
        </div>
        <input ref={receiptRef} type="file" accept="image/*,application/pdf" className="hidden"
          onChange={(e) => { if (e.target.files?.[0]) { uploadPhoto(e.target.files[0], "receipt"); e.target.value = ""; }}}/>
      </div>
      {stockOpen && <StockPickerModal jobId={job.id} onClose={() => setStockOpen(false)} onUsed={onReload}/>}
    </div>
  );
}

function StockPickerModal({ jobId, onClose, onUsed }) {
  const [parts, setParts] = useState([]);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(null);
  const [qty, setQty] = useState("1");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/parts").then(r => setParts(r.data)).catch(() => {}); }, []);
  const filtered = parts.filter(p => {
    const s = q.toLowerCase();
    return !s || p.name.toLowerCase().includes(s) || (p.sku || "").toLowerCase().includes(s) || (p.category || "").toLowerCase().includes(s);
  });
  const use = async () => {
    if (!picked || !qty) return;
    setBusy(true);
    try {
      await api.post(`/jobs/${jobId}/use-part`, { part_id: picked.id, quantity: parseFloat(qty) || 1 });
      toast.success(`${picked.name} added at $${(picked.sell_price || 0).toFixed(2)}`);
      onUsed();
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not add part"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="stock-picker-modal">
      <div className="w-full sm:max-w-md bg-white rugged-card p-5 sm:my-4 max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between flex-shrink-0">
          <h3 className="font-display font-black text-xl">Truck Stock</h3>
          <button onClick={onClose} className="text-slate-500"><Trash weight="bold" size={20}/></button>
        </div>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search parts..." data-testid="stock-search"
          className="mt-3 w-full h-11 px-3 border-2 border-[#0f172a] text-sm font-medium flex-shrink-0"/>
        <div className="mt-3 flex-1 overflow-y-auto space-y-1.5" data-testid="stock-list">
          {parts.length === 0 && (
            <div className="text-center py-8 text-sm text-slate-500 font-medium">
              No parts in catalog yet. Admin can add parts in Settings → Parts Catalog.
            </div>
          )}
          {filtered.map(p => (
            <button key={p.id} onClick={() => setPicked(p)} data-testid={`stock-part-${p.id}`}
              className={`w-full text-left p-3 border-2 ${picked?.id === p.id ? "border-[#00C4CC] bg-cyan-50" : "border-[#0f172a] bg-white hover:bg-slate-50"}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-display font-black text-sm">{p.name}</div>
                  {p.sku && <div className="text-[11px] font-mono text-slate-500">SKU {p.sku}</div>}
                  {p.category && <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mt-0.5">{p.category}</div>}
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="font-display font-black text-base text-[#00C4CC]">${(p.sell_price || 0).toFixed(2)}</div>
                  <div className="text-[10px] text-slate-400 uppercase font-bold">/ea</div>
                </div>
              </div>
            </button>
          ))}
        </div>
        {picked && (
          <div className="mt-3 pt-3 border-t-2 border-[#0f172a] flex-shrink-0" data-testid="stock-confirm">
            <div className="text-xs font-black uppercase tracking-wider">Adding: {picked.name}</div>
            <div className="mt-2 flex items-center gap-2">
              <input type="number" step="0.5" min="0.5" value={qty} onChange={e => setQty(e.target.value)}
                data-testid="stock-qty"
                className="w-24 h-11 px-2 border-2 border-[#0f172a] text-sm font-medium"/>
              <span className="text-sm font-bold">× ${(picked.sell_price || 0).toFixed(2)} = ${(parseFloat(qty || 1) * (picked.sell_price || 0)).toFixed(2)}</span>
            </div>
            <button onClick={use} disabled={busy} data-testid="stock-use-btn"
              className="tactile-btn mt-3 w-full h-12 bg-[#00C4CC] text-white font-black uppercase tracking-wider disabled:opacity-50">
              {busy ? "Adding..." : "Add to Job"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function AIPanel({ jobId, appliance, disabled }) {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/ai/history`, { params: { session_id: jobId }}).then(r => setMsgs(r.data)).catch(() => {});
  }, [jobId]);

  const send = async () => {
    if (!input.trim()) return;
    const question = input.trim();
    setInput("");
    setMsgs(m => [...m, { role: "user", text: question }]);
    setSending(true);
    try {
      const { data } = await api.post("/ai/diagnose", { job_id: jobId, message: question, appliance_type: appliance });
      setMsgs(m => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSending(false); }
  };

  return (
    <div className="mt-5">
      <div className="rugged-card p-4 min-h-[300px] max-h-[500px] overflow-y-auto space-y-3" data-testid="ai-chat">
        {msgs.length === 0 && (
          <div className="text-center text-zinc-500 py-8">
            <ChatCircle weight="bold" size={40} className="mx-auto opacity-40"/>
            <p className="mt-3 font-bold">Describe the problem you're seeing</p>
            <p className="text-xs mt-1">e.g., "Fridge not cooling but freezer works"</p>
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] px-3 py-2 border-2 border-[#09090b] whitespace-pre-wrap text-sm ${
              m.role === "user" ? "bg-[#00C4CC] text-white" : "bg-white"
            }`}>{m.text}</div>
          </div>
        ))}
        {sending && <div className="text-zinc-500 font-bold text-sm">Diagnosing...</div>}
      </div>
      <div className="mt-3 flex gap-2">
        <input value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && send()}
          placeholder="What's the symptom?" data-testid="ai-input"
          className="flex-1 h-14 px-3 border-2 border-[#09090b] font-medium"/>
        <button onClick={send} disabled={sending} data-testid="ai-send"
          className="tactile-btn h-14 px-5 bg-[#00C4CC] text-white">
          <PaperPlaneTilt weight="bold" size={22}/>
        </button>
      </div>
    </div>
  );
}

function WorkPerformedSection({ job, onReload, disabled }) {
  const { t } = useT();
  const [text, setText] = useState(job.work_performed || "");
  const [polished, setPolished] = useState(job.work_performed_polished || "");
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/jobs/${job.id}/work-performed`, { text });
      toast.success("Work notes saved");
      await onReload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const polish = async () => {
    if (!text.trim()) return toast.error("Type your rough notes first");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/polish", { text, job_id: job.id });
      setPolished(data.polished);
      toast.success("Polished write-up ready");
      await onReload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const copy = () => {
    navigator.clipboard.writeText(polished);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="rugged-card p-4">
      <div className="flex items-center gap-2">
        <Notepad weight="bold" size={20}/>
        <h3 className="font-display font-black text-lg uppercase">{t("work.title")}</h3>
      </div>
      <p className="mt-1 text-xs text-zinc-500 font-medium">
        Jot down what you did on-site. Tap <b>{t("work.polish")}</b> to turn rough notes into a customer-ready write-up.
      </p>
      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        disabled={disabled}
        placeholder={t("work.placeholder")}
        rows={5}
        data-testid="work-performed-input"
        className="mt-3 w-full px-3 py-2 border-2 border-[#09090b] font-medium disabled:bg-zinc-100"
      />
      {!disabled && (
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <VoiceRecorder disabled={disabled} onTranscript={(t) => setText(prev => prev ? `${prev}\n${t}` : t)}/>
          <span className="text-xs text-slate-500 font-medium">{t("work.dictate_hint")}</span>
        </div>
      )}
      <div className="mt-3 grid grid-cols-2 gap-2">
        <button onClick={save} disabled={saving || disabled || !text.trim()} data-testid="save-work-btn"
          className="h-12 border-2 border-[#09090b] font-black uppercase text-sm tracking-wider disabled:opacity-50">
          {saving ? t("work.saving") : t("work.save")}
        </button>
        <button onClick={polish} disabled={busy || disabled || !text.trim()} data-testid="polish-work-btn"
          className="tactile-btn h-12 bg-[#09090b] text-white font-black uppercase text-sm tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-50">
          <Sparkle weight="fill" size={16}/> {busy ? t("work.polishing") : t("work.polish")}
        </button>
      </div>
      {polished && (
        <div className="mt-4 p-3 bg-[#ecfeff] border-2 border-[#00C4CC]" data-testid="polished-writeup">
          <div className="flex items-center justify-between">
            <div className="text-xs font-black uppercase tracking-wider text-[#0e7490] flex items-center gap-1">
              <Sparkle weight="fill" size={14}/> {t("work.ai_writeup")}
            </div>
            <button onClick={copy} className="text-xs font-bold uppercase underline">{t("work.copy")}</button>
          </div>
          <p className="mt-2 whitespace-pre-wrap text-sm">{polished}</p>
        </div>
      )}
    </div>
  );
}

function countPhotosForChecklist(job, checklistId) {
  let n = 0;
  for (const bucket of ["before_photos", "after_photos", "photos"]) {
    for (const p of (job?.[bucket] || [])) {
      if (p && typeof p === "object" && p.checklist_id === checklistId) n++;
    }
  }
  return n;
}

function ChecklistPanel({ job, onReload, disabled }) {
  const { t } = useT();
  const [newItem, setNewItem] = useState("");
  const items = job.checklist || [];
  const doneCount = items.filter(i => i.done).length;

  const add = async () => {
    if (!newItem.trim()) return;
    try {
      await api.post(`/jobs/${job.id}/checklist`, { text: newItem.trim() });
      setNewItem("");
      onReload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggle = async (item) => {
    // Client-side pre-check: block if trying to mark done without enough photos.
    // Backend also enforces this — this is just for a faster UX message.
    const required = Number(item.min_photos || 0);
    if (!item.done && required > 0) {
      const attached = countPhotosForChecklist(job, item.id);
      if (attached < required) {
        toast.error(`Attach ${required - attached} more photo${required - attached === 1 ? "" : "s"} to this step first (${attached}/${required}).`);
        return;
      }
    }
    try {
      await api.patch(`/jobs/${job.id}/checklist/${item.id}`, { done: !item.done });
      onReload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (item) => {
    await api.delete(`/jobs/${job.id}/checklist/${item.id}`);
    onReload();
  };

  return (
    <div className="mt-5 space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-black text-lg uppercase">{t("check.title")}</h3>
        <span className="font-display font-black text-lg">{doneCount}/{items.length}</span>
      </div>

      {items.length === 0 && (
        <div className="p-6 dashed-drop text-center">
          <ListChecks weight="bold" size={36} className="mx-auto opacity-40"/>
          <p className="mt-2 font-bold text-slate-600">{t("check.empty")}</p>
        </div>
      )}

      <div className="space-y-2">
        {items.map(item => {
          const required = Number(item.min_photos || 0);
          const attached = required > 0 ? countPhotosForChecklist(job, item.id) : 0;
          const meets = required === 0 || attached >= required;
          return (
          <div key={item.id} data-testid={`checklist-item-${item.id}`}
            className={`rugged-card p-3 flex items-center gap-3 ${item.done ? "bg-green-50" : (required > 0 && !meets ? "bg-amber-50" : "")}`}>
            <button
              onClick={() => !disabled && toggle(item)}
              disabled={disabled}
              data-testid={`checklist-toggle-${item.id}`}
              className="flex-shrink-0 disabled:opacity-40">
              {item.done
                ? <CheckSquare weight="fill" size={28} className="text-[#00C4CC]"/>
                : <Square weight="bold" size={28}/>}
            </button>
            <div className={`flex-1 min-w-0 ${item.done ? "line-through text-slate-500" : ""}`}>
              <div className="font-bold">{item.text}</div>
              {required > 0 && !item.done && (
                <div className={`text-xs font-bold inline-flex items-center gap-1 mt-0.5 ${meets ? "text-emerald-700" : "text-amber-700"}`}
                  data-testid={`checklist-photos-${item.id}`}>
                  <Camera weight="fill" size={11}/>
                  {attached}/{required} photos
                  {meets ? <span className="text-emerald-600">· ready to check off</span>
                         : <span>· attach {required - attached} more</span>}
                </div>
              )}
              {item.done && item.done_at && (
                <div className="text-xs text-slate-500">{t("check.fixed")} {new Date(item.done_at).toLocaleTimeString()}</div>
              )}
            </div>
            {!disabled && (
              <button onClick={() => del(item)} data-testid={`checklist-del-${item.id}`} className="p-2 text-red-600">
                <Trash weight="bold" size={16}/>
              </button>
            )}
          </div>
          );
        })}
      </div>

      {!disabled && (
        <div className="flex gap-2">
          <input
            value={newItem}
            onChange={e => setNewItem(e.target.value)}
            onKeyDown={e => e.key === "Enter" && add()}
            placeholder={t("check.new_placeholder")}
            data-testid="checklist-new-input"
            className="flex-1 h-12 px-3 border-2 border-[#0f172a] font-medium"/>
          <button onClick={add} disabled={!newItem.trim()} data-testid="checklist-add-btn"
            className="tactile-btn h-12 px-5 bg-[#0f172a] text-white font-black uppercase text-sm tracking-wider disabled:opacity-50">
            <Plus weight="bold" size={18}/>
          </button>
        </div>
      )}
    </div>
  );
}


function AppliancesPanel({ job, onReload, disabled }) {
  const { t } = useT();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const appliances = job.appliances || [];

  const scan = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const compressed = await compressImage(file);
      const fd = new FormData();
      fd.append("file", compressed);
      const { data } = await api.post(`/jobs/${job.id}/scan-nameplate`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.parsed) {
        const a = data.appliance;
        toast.success(`Scanned: ${a.brand || "unknown brand"} ${a.model || ""}`.trim());
      } else {
        toast.warning("Saved, but AI couldn't parse the label cleanly — you can retake or edit later");
      }
      await onReload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Scan failed");
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm(t("app.confirm_remove"))) return;
    try {
      await api.delete(`/jobs/${job.id}/appliance/${id}`);
      toast.success(t("app.removed"));
      await onReload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="mt-5 space-y-4" data-testid="appliances-panel">
      <div className="rugged-card p-5 bg-white">
        <div className="flex items-center gap-2">
          <Barcode weight="bold" size={22} className="text-[#00C4CC]"/>
          <h3 className="font-display font-black text-lg uppercase">{t("app.scanner")}</h3>
        </div>
        <p className="mt-1 text-sm text-slate-600 font-medium">
          {t("app.scanner_sub")}
        </p>
        {!disabled && (
          <>
            <button
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              data-testid="scan-nameplate-btn"
              className="tactile-btn mt-4 w-full h-14 bg-[#00C4CC] text-white font-black uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {busy ? (
                <>
                  <Sparkle weight="fill" size={20} className="animate-pulse"/> Reading Nameplate...
                </>
              ) : (
                <>
                  <Camera weight="bold" size={20}/> Scan Nameplate
                </>
              )}
            </button>
            <button
              onClick={() => setBulkOpen(true)}
              disabled={busy}
              data-testid="bulk-appliance-btn"
              className="tactile-btn mt-2 w-full h-12 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-black uppercase tracking-wider text-xs inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Sparkle weight="fill" size={16}/> Bulk Scan — Log All Appliances at Once
            </button>
            <button
              onClick={() => setVoiceOpen(true)}
              disabled={busy}
              data-testid="voice-walk-btn"
              className="tactile-btn mt-2 w-full h-12 bg-gradient-to-r from-red-600 via-fuchsia-600 to-violet-600 text-white font-black uppercase tracking-wider text-xs inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Sparkle weight="fill" size={16}/> Voice Walk — Narrate & Auto-Log
            </button>
          </>
        )}
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
          data-testid="scan-nameplate-input"
          onChange={(e) => { if (e.target.files?.[0]) { scan(e.target.files[0]); e.target.value = ""; } }}/>
      </div>

      {appliances.length === 0 ? (
        <div className="rugged-card p-8 text-center text-slate-500 font-medium">
          No appliances scanned yet. Tap "Scan Nameplate" above to capture your first one.
        </div>
      ) : (
        appliances.map(a => (
          <div key={a.id} className="rugged-card p-4 bg-white" data-testid={`appliance-${a.id}`}>
            <div className="flex gap-3">
              <PhotoOpen photo={a}
                className="w-24 h-24 bg-slate-100 border-2 border-[#0f172a] flex-shrink-0 overflow-hidden">
                <img src={fileUrl(a.path)} alt="nameplate" className="w-full h-full object-cover"/>
              </PhotoOpen>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-display font-black text-base leading-tight">
                      {a.brand || "Unknown brand"} {a.type && <span className="text-slate-500 font-bold text-sm">· {a.type}</span>}
                    </div>
                    {a.model && <div className="text-xs font-bold text-slate-600 mt-0.5">Model {a.model}</div>}
                    {a.serial && <div className="text-xs font-mono text-slate-600 mt-0.5">S/N {a.serial}</div>}
                  </div>
                  {!disabled && (
                    <button onClick={() => del(a.id)} data-testid={`appliance-delete-${a.id}`}
                      className="text-red-500 hover:text-red-700 p-1 -m-1 flex-shrink-0">
                      <Trash weight="bold" size={16}/>
                    </button>
                  )}
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {a.estimated_age_years != null && (
                    <span className={`px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ${
                      a.estimated_age_years >= 15 ? "bg-red-500 text-white" :
                      a.estimated_age_years >= 8 ? "bg-yellow-400 text-[#0f172a]" :
                      "bg-green-500 text-white"
                    }`} data-testid={`appliance-age-${a.id}`}>
                      ~{a.estimated_age_years} yr old
                    </span>
                  )}
                  {a.manufacture_date && (
                    <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-slate-100 border border-slate-300">
                      Mfg {a.manufacture_date}
                    </span>
                  )}
                  {a.capacity && (
                    <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-slate-100 border border-slate-300">
                      {a.capacity}
                    </span>
                  )}
                  {a.voltage && (
                    <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-slate-100 border border-slate-300">
                      {a.voltage}
                    </span>
                  )}
                </div>
                {a.specs && <div className="mt-2 text-xs text-slate-600 font-medium">{a.specs}</div>}
                {a.notes && <div className="mt-1 text-xs text-slate-500 italic">{a.notes}</div>}
                {!a.brand && !a.model && !a.serial && a.raw_reply && (
                  <details className="mt-2">
                    <summary className="text-xs font-bold text-slate-500 cursor-pointer">AI couldn't parse — see raw reading</summary>
                    <div className="mt-1 text-xs text-slate-600 whitespace-pre-wrap p-2 bg-slate-50 border border-slate-200">{a.raw_reply}</div>
                  </details>
                )}
              </div>
            </div>
          </div>
        ))
      )}
      {bulkOpen && (
        <BulkApplianceScan
          jobId={job.id}
          onClose={() => setBulkOpen(false)}
          onSaved={onReload}
        />
      )}
      {voiceOpen && (
        <VoiceWalk
          jobId={job.id}
          onClose={() => setVoiceOpen(false)}
          onSaved={onReload}
        />
      )}
    </div>
  );
}



function ClickUpAttachBanner({ job }) {
  const buckets = [
    ...(job.before_photos || []).map(p => ({ ...p, bucket: "before" })),
    ...(job.after_photos  || []).map(p => ({ ...p, bucket: "after"  })),
    ...(job.receipts      || []).map(p => ({ ...p, bucket: "receipt" })),
  ];
  const cu = buckets.filter(p => p.source === "clickup" && p.clickup_task_id);
  if (cu.length === 0) return null;

  const byTask = cu.reduce((acc, p) => {
    (acc[p.clickup_task_id] = acc[p.clickup_task_id] || []).push(p);
    return acc;
  }, {});
  const taskIds = Object.keys(byTask);
  const newest = cu.reduce((a, b) => (a.uploaded_at > b.uploaded_at ? a : b));

  return (
    <div className="mt-4 p-3 border-2 border-fuchsia-300 bg-fuchsia-50 flex items-center gap-3 flex-wrap"
      data-testid="clickup-attach-banner">
      <ClipboardText weight="fill" size={18} className="text-fuchsia-600 flex-none"/>
      <div className="flex-1 min-w-0 text-xs">
        <span className="font-black uppercase tracking-widest text-[10px] text-fuchsia-800">Auto-Attached From ClickUp</span>
        <span className="ml-2 text-slate-800">
          <b>{cu.length}</b> file{cu.length === 1 ? "" : "s"} from <b>{taskIds.length}</b> task{taskIds.length === 1 ? "" : "s"}
          {newest?.uploaded_at && (
            <span className="ml-1 text-slate-500">· newest {new Date(newest.uploaded_at).toLocaleDateString()}</span>
          )}
        </span>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {taskIds.slice(0, 4).map(tid => (
          <a key={tid} href={`https://app.clickup.com/t/${tid}`} target="_blank" rel="noreferrer"
            data-testid={`clickup-task-link-${tid}`}
            className="h-7 px-2 bg-white border-2 border-fuchsia-400 hover:bg-fuchsia-100 text-fuchsia-800 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
            Task · {byTask[tid].length} {byTask[tid].length === 1 ? "file" : "files"}
          </a>
        ))}
        {taskIds.length > 4 && (
          <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
            + {taskIds.length - 4} more
          </span>
        )}
      </div>
    </div>
  );
}
